import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { setPageTitle } from '../Utils/brand';
import ContactForm from '../Components/ContactForm';
import TablePagination from '../Components/TablePagination';
import { getAuthConfig } from '../Utils/authConfig';
import { 
    SearchIcon, 
    CrossIcon, 
    LockIcon, 
    CheckIcon, 
    RefreshIcon, 
    TrashIcon, 
    ShieldIcon, 
    ZapIcon, 
    TagIcon, 
    DogIcon, 
    ClockIcon,
    SettingsIcon,
    GlobeIcon
} from '../Components/Icons';

// Deep case-insensitive search across all properties and nested JSON data
const matchesDeepSearch = (item, query) => {
    if (!query || !query.trim()) return true;
    const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return true;

    const collected = [];
    const extract = (val) => {
        if (val === null || val === undefined) return;
        if (typeof val === 'object') {
            if (Array.isArray(val)) {
                val.forEach(extract);
            } else {
                Object.values(val).forEach(extract);
            }
            return;
        }
        collected.push(String(val).toLowerCase());
    };

    extract(item);

    // Deep inspect any nested JSON strings (e.g. telegram_user, assigned_ns, details)
    ['telegram_user', 'assigned_ns', 'details'].forEach(prop => {
        if (item[prop] && typeof item[prop] === 'string' && (item[prop].startsWith('{') || item[prop].startsWith('['))) {
            try {
                extract(JSON.parse(item[prop]));
            } catch (_) {}
        }
    });

    // Append human-friendly derived keywords for statuses and options
    if (item.status !== undefined) {
        const numStatus = Number(item.status);
        if (numStatus === 2) collected.push('moderation', 'under moderation', 'pending moderation');
        else if (numStatus === 1) collected.push('parked', 'active', 'verified');
        else if (numStatus === 0) collected.push('offline', 'pending');
        else if (numStatus === -1) collected.push('inactive', 'disabled');
        else if (item.status === 'approved') collected.push('approved', 'processed');
        else if (item.status === 'rejected') collected.push('rejected');
        else if (item.status === 'pending') collected.push('pending', 'awaiting');
    }
    if (item.is_sold > 0) collected.push('sold');
    if (item.disabled !== undefined) {
        collected.push(item.disabled === 1 ? 'disabled blocked inactive' : 'active enabled allowed');
    }
    if (item.action_type) {
        collected.push(String(item.action_type).toLowerCase(), 'order');
    }
    if (item.type === 'description_update') {
        collected.push('description', 'update', 'text');
    } else if (item.type === 'new_domain') {
        collected.push('new', 'domain');
    }

    const unifiedText = collected.join(' ');
    return tokens.every(token => unifiedText.includes(token));
};

// Sorters for each tab's data
const sortDomains = (list, sortKey) => {
    return [...list].sort((a, b) => {
        switch (sortKey) {
            case 'id-asc':
                return (a.id || 0) - (b.id || 0);
            case 'name-az':
                return (a.name || '').localeCompare(b.name || '');
            case 'name-za':
                return (b.name || '').localeCompare(a.name || '');
            case 'price-high':
                return (Number(b.pricebuy) || 0) - (Number(a.pricebuy) || 0);
            case 'price-low':
                return (Number(a.pricebuy) || 0) - (Number(b.pricebuy) || 0);
            case 'status':
                return (b.status ?? 0) - (a.status ?? 0);
            case 'id-desc':
            default:
                return (b.id || 0) - (a.id || 0);
        }
    });
};

const sortSubmissions = (list, sortKey) => {
    return [...list].sort((a, b) => {
        switch (sortKey) {
            case 'date-asc':
                return new Date(a.created_at || 0) - new Date(b.created_at || 0);
            case 'pending-first': {
                const aP = a.status === 'pending' ? 1 : 0;
                const bP = b.status === 'pending' ? 1 : 0;
                if (aP !== bP) return bP - aP;
                return new Date(b.created_at || 0) - new Date(a.created_at || 0);
            }
            case 'domain-az':
                return (a.domain_name || '').localeCompare(b.domain_name || '');
            case 'date-desc':
            default:
                return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        }
    });
};

const sortUsers = (list, sortKey) => {
    return [...list].sort((a, b) => {
        switch (sortKey) {
            case 'date-asc':
                return (a.id || 0) - (b.id || 0);
            case 'name-az': {
                const nameA = `${a.first_name || ''} ${a.last_name || ''}`.trim();
                const nameB = `${b.first_name || ''} ${b.last_name || ''}`.trim();
                return nameA.localeCompare(nameB);
            }
            case 'last-active':
                return new Date(b.last_time || 0) - new Date(a.last_time || 0);
            case 'active-first':
                return (a.disabled || 0) - (b.disabled || 0);
            case 'date-desc':
            default:
                return (b.id || 0) - (a.id || 0);
        }
    });
};

const sortActions = (list, sortKey) => {
    return [...list].sort((a, b) => {
        switch (sortKey) {
            case 'date-asc':
                return new Date(a.start_time || 0) - new Date(b.start_time || 0);
            case 'amount-high':
                return (Number(b.price_paid) || 0) - (Number(a.price_paid) || 0);
            case 'domain-az':
                return (a.domain_name || '').localeCompare(b.domain_name || '');
            case 'date-desc':
            default:
                return new Date(b.start_time || 0) - new Date(a.start_time || 0);
        }
    });
};

const sortActivity = (list, sortKey) => {
    return [...list].sort((a, b) => {
        switch (sortKey) {
            case 'date-asc':
                return new Date(a.timestamp || 0) - new Date(b.timestamp || 0);
            case 'user-az': {
                const uA = `${a.first_name || ''} ${a.last_name || ''}`.trim();
                const uB = `${b.first_name || ''} ${b.last_name || ''}`.trim();
                return uA.localeCompare(uB);
            }
            case 'action-az':
                return (a.action || '').localeCompare(b.action || '');
            case 'date-desc':
            default:
                return new Date(b.timestamp || 0) - new Date(a.timestamp || 0);
        }
    });
};

// Reusable table search and filter bar
const AdminTableFilterBar = ({
    searchTerm,
    setSearchTerm,
    sortBy,
    setSortBy,
    sortOptions = [],
    resultsCount,
    totalCount,
    itemLabel = 'records',
    placeholder = 'Search by any field, name, user, ID, price, status...',
    onReset
}) => {
    const isFiltered = Boolean(searchTerm || (sortBy && sortOptions[0] && sortBy !== sortOptions[0].value));

    return (
        <div className="space-y-3 mb-6 bg-dark-200/40 p-3.5 sm:p-4 rounded-xl border border-dark-300/60">
            <div className="flex flex-col sm:flex-row gap-3">
                {/* Search Input with ✕ clear button */}
                <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-neutral-400">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                    <input
                        type="text"
                        className="input !pl-10 pr-10 py-2.5 w-full text-sm bg-dark-100/90 border border-dark-300/80 focus:border-primary-500 rounded-xl"
                        placeholder={placeholder}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Escape' && searchTerm) {
                                e.preventDefault();
                                e.stopPropagation();
                                setSearchTerm('');
                            }
                        }}
                    />
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-neutral-400 hover:text-white transition-colors cursor-pointer"
                            title="Clear search"
                            aria-label="Clear search"
                        >
                            <span className="w-5 h-5 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
                                <CrossIcon className="w-2.5 h-2.5 text-neutral-300" />
                            </span>
                        </button>
                    )}
                </div>

                {/* Sort selector dropdown */}
                {sortOptions.length > 0 && (
                    <div className="flex items-center gap-2">
                        <div className="relative">
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="input px-3.5 py-2.5 text-sm bg-dark-100/90 border border-dark-300/80 focus:border-primary-500 rounded-xl cursor-pointer text-white appearance-none pr-8 min-w-[170px]"
                            >
                                {sortOptions.map(opt => (
                                    <option key={opt.value} value={opt.value} className="bg-dark-100 text-white">
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                            <div className="absolute inset-y-0 right-0 pr-2.5 flex items-center pointer-events-none text-neutral-400">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Counter and Reset Button */}
            <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2.5">
                    <span className="text-neutral-400">
                        Showing <strong className="text-white">{resultsCount}</strong> of <strong className="text-neutral-300">{totalCount}</strong> {itemLabel}
                    </span>
                    {isFiltered && (
                        <button
                            type="button"
                            onClick={() => {
                                setSearchTerm('');
                                if (onReset) onReset();
                            }}
                            className="px-2.5 py-1 rounded-md bg-white/5 hover:bg-white/10 text-primary-400 hover:text-primary-300 border border-white/10 transition-colors flex items-center gap-1.5 cursor-pointer font-medium"
                            title="Reset all filters"
                        >
                            <CrossIcon className="w-3 h-3 inline-block" />
                            <span>Reset filter</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// Reusable table empty state
const AdminTableEmptyState = ({
    colSpan,
    searchTerm,
    onReset,
    itemLabel = 'records',
    emptyMessage = 'No records found'
}) => (
    <tr>
        <td colSpan={colSpan} className="py-12 px-4 text-center">
            <div className="max-w-md mx-auto space-y-3.5 p-6 rounded-2xl bg-dark-100/40 border border-dark-300/40">
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mx-auto text-primary-400">
                    <SearchIcon className="w-6 h-6" />
                </div>
                <div className="space-y-1">
                    <h4 className="text-base font-bold text-white">
                        {searchTerm ? 'No matching records found' : emptyMessage}
                    </h4>
                    <p className="text-xs text-neutral-400">
                        {searchTerm ? (
                            <>No {itemLabel} matching <span className="text-primary-400 font-medium">"{searchTerm}"</span> across table fields</>
                        ) : (
                            `No ${itemLabel} available in this table.`
                        )}
                    </p>
                </div>
                {searchTerm && (
                    <button
                        type="button"
                        onClick={onReset}
                        className="btn btn-primary px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5 shadow-md shadow-primary-500/20 active:scale-95 transition-all"
                    >
                        <CrossIcon className="w-3.5 h-3.5" />
                        <span>Clear Filter & Show All {itemLabel}</span>
                    </button>
                )}
            </div>
        </td>
    </tr>
);


const AdminPanelPage = () => {
    const { adminUrl } = useParams(); // Get the admin URL from the URL parameters
    const [isAdmin, setIsAdmin] = useState(false);
    const [adminToken, setAdminToken] = useState('');
    const [currentUser, setCurrentUser] = useState(() => {
        try {
            const raw = localStorage.getItem('telegram_user') || localStorage.getItem('telegramUser');
            return raw ? JSON.parse(raw) : null;
        } catch (_) {
            return null;
        }
    });
    const [domains, setDomains] = useState([]);
    const [users, setUsers] = useState([]);
    const [actions, setActions] = useState([]);
    const [activityLogs, setActivityLogs] = useState([]);
    const [editingDomain, setEditingDomain] = useState(null);
    const [editingAction, setEditingAction] = useState(null);
    const [submissions, setSubmissions] = useState([]);
    const [editForm, setEditForm] = useState({ 
        name: '', 
        description: '', 
        enable_buy: true,
        pricebuy: '', 
        discount_buy: '', 
        enable_rent: true,
        pricerent: '', 
        discount_rent: '', 
        enable_forward: true,
        pricefw: '', 
        discount_fw: '', 
        owner_id: '', 
        status: 1 
    });
    const [editActionForm, setEditActionForm] = useState({ destination: '', end_time: '' });
    const [showEditModal, setShowEditModal] = useState(false);
    const [showEditActionModal, setShowEditActionModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [ownerSearchQuery, setOwnerSearchQuery] = useState('');

    // Tab-specific search and sort states
    const [searchTerms, setSearchTerms] = useState({
        domains: '',
        submissions: '',
        users: '',
        actions: '',
        activity: ''
    });
    const [sortTerms, setSortTerms] = useState({
        domains: 'id-desc',
        submissions: 'date-desc',
        users: 'date-desc',
        actions: 'date-desc',
        activity: 'date-desc'
    });
    const [currentPage, setCurrentPage] = useState({
        domains: 1,
        submissions: 1,
        users: 1,
        actions: 1,
        activity: 1
    });

    const handleSearchChange = (tab, val) => {
        setSearchTerms(prev => ({ ...prev, [tab]: val }));
        setCurrentPage(prev => ({ ...prev, [tab]: 1 }));
    };
    const handleSortChange = (tab, val) => {
        setSortTerms(prev => ({ ...prev, [tab]: val }));
        setCurrentPage(prev => ({ ...prev, [tab]: 1 }));
    };
    const handleResetTab = (tab, defaultSort = 'date-desc') => {
        setSearchTerms(prev => ({ ...prev, [tab]: '' }));
        setSortTerms(prev => ({ ...prev, [tab]: defaultSort }));
        setCurrentPage(prev => ({ ...prev, [tab]: 1 }));
    };

    const handlePageChange = (tab, page) => {
        setCurrentPage(prev => ({ ...prev, [tab]: page }));
    };

    // Filtered and sorted datasets for each tab
    const filteredDomains = useMemo(() => {
        const filtered = domains.filter(d => matchesDeepSearch(d, searchTerms.domains));
        return sortDomains(filtered, sortTerms.domains);
    }, [domains, searchTerms.domains, sortTerms.domains]);

    const filteredSubmissions = useMemo(() => {
        const filtered = submissions.filter(s => matchesDeepSearch(s, searchTerms.submissions));
        return sortSubmissions(filtered, sortTerms.submissions);
    }, [submissions, searchTerms.submissions, sortTerms.submissions]);

    const filteredUsers = useMemo(() => {
        const filtered = users.filter(u => matchesDeepSearch(u, searchTerms.users));
        return sortUsers(filtered, sortTerms.users);
    }, [users, searchTerms.users, sortTerms.users]);

    const filteredActions = useMemo(() => {
        const filtered = actions.filter(a => matchesDeepSearch(a, searchTerms.actions));
        return sortActions(filtered, sortTerms.actions);
    }, [actions, searchTerms.actions, sortTerms.actions]);

    const filteredActivity = useMemo(() => {
        const filtered = activityLogs.filter(l => matchesDeepSearch(l, searchTerms.activity));
        return sortActivity(filtered, sortTerms.activity);
    }, [activityLogs, searchTerms.activity, sortTerms.activity]);

    // Paginated slices (max 100 records per page)
    const PAGE_SIZE = 100;
    const paginatedDomains = useMemo(() => {
        const start = (currentPage.domains - 1) * PAGE_SIZE;
        return filteredDomains.slice(start, start + PAGE_SIZE);
    }, [filteredDomains, currentPage.domains]);

    const paginatedSubmissions = useMemo(() => {
        const start = (currentPage.submissions - 1) * PAGE_SIZE;
        return filteredSubmissions.slice(start, start + PAGE_SIZE);
    }, [filteredSubmissions, currentPage.submissions]);

    const paginatedUsers = useMemo(() => {
        const start = (currentPage.users - 1) * PAGE_SIZE;
        return filteredUsers.slice(start, start + PAGE_SIZE);
    }, [filteredUsers, currentPage.users]);

    const paginatedActions = useMemo(() => {
        const start = (currentPage.actions - 1) * PAGE_SIZE;
        return filteredActions.slice(start, start + PAGE_SIZE);
    }, [filteredActions, currentPage.actions]);

    const paginatedActivity = useMemo(() => {
        const start = (currentPage.activity - 1) * PAGE_SIZE;
        return filteredActivity.slice(start, start + PAGE_SIZE);
    }, [filteredActivity, currentPage.activity]);

    // Fast search and lookup for Owner ID selection in Edit Domain modal
    const filteredOwnerUsers = useMemo(() => {
        if (!ownerSearchQuery.trim()) return users;
        const q = ownerSearchQuery.toLowerCase().trim();
        return users.filter(u => 
            String(u.id) === q ||
            (u.first_name && u.first_name.toLowerCase().includes(q)) ||
            (u.last_name && u.last_name.toLowerCase().includes(q)) ||
            (u.username && u.username.toLowerCase().includes(q)) ||
            (u.telegram_id && String(u.telegram_id).toLowerCase().includes(q))
        );
    }, [users, ownerSearchQuery]);

    const selectedOwnerUser = useMemo(() => {
        if (!editForm.owner_id) return null;
        return users.find(u => String(u.id) === String(editForm.owner_id)) || null;
    }, [users, editForm.owner_id]);

    const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
    const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();

    // Read ?tab= from URL, defaulting to 'domains'
    const getTabFromUrl = useCallback(() => {
        const params = new URLSearchParams(location.search);
        const tabParam = params.get('tab');
        const validTabs = ['domains', 'submissions', 'users', 'actions', 'activity', 'settings'];
        if (tabParam && validTabs.includes(tabParam)) {
            return tabParam;
        }
        return 'domains';
    }, [location.search]);

    const [activeTab, setActiveTab] = useState(getTabFromUrl);

    // Sync active tab when location.search changes
    useEffect(() => {
        setActiveTab(getTabFromUrl());
    }, [getTabFromUrl]);

    // Handle tab switching while keeping URL query params in sync
    const handleTabChange = useCallback((newTab) => {
        setActiveTab(newTab);
        const params = new URLSearchParams(location.search);
        params.set('tab', newTab);
        navigate({ search: params.toString() }, { replace: true });
    }, [location.search, navigate]);

    const [accessDenied, setAccessDenied] = useState(false);
    const [isValidating, setIsValidating] = useState(!window.location.hostname.includes('localhost') && !window.location.hostname.includes('127.0.0.1'));

    const [isCheckingNow, setIsCheckingNow] = useState(false);
    const [watchdogInterval, setWatchdogInterval] = useState(() => {
        try {
            const saved = localStorage.getItem("domainer_watchdog_interval");
            return saved ? parseInt(saved, 10) : 60;
        } catch (_) { return 60; }
    });
    const [watchdogHours, setWatchdogHours] = useState(() => {
        try {
            const saved = localStorage.getItem("domainer_watchdog_hours");
            return saved ? parseFloat(saved) : 168;
        } catch (_) { return 168; }
    });
    const [cloudflareConfigured, setCloudflareConfigured] = useState(false);
    const [cloudflareIp, setCloudflareIp] = useState('');
    const [isSavingCfIp, setIsSavingCfIp] = useState(false);
    const [cloudflareStatus, setCloudflareStatus] = useState(null);
    const [systemSettings, setSystemSettings] = useState(null);
    const [isSyncingCf, setIsSyncingCf] = useState(false);
    const [cfSyncMessage, setCfSyncMessage] = useState('');
    const [cfIpSaveMessage, setCfIpSaveMessage] = useState('');
    const [minPrices, setMinPrices] = useState({ min_price_buy: 100, min_price_rent: 50, min_price_forward: 10 });

    // Watchdog guide collapsed state (persisted in localStorage, defaults to true)
    const [isWatchdogCollapsed, setIsWatchdogCollapsed] = useState(() => {
        try {
            const saved = localStorage.getItem('domainer_watchdog_collapsed');
            return saved !== null ? saved === 'true' : true;
        } catch (_) {
            return true;
        }
    });

    const toggleWatchdogCollapsed = () => {
        setIsWatchdogCollapsed(prev => {
            const nextVal = !prev;
            try {
                localStorage.setItem('domainer_watchdog_collapsed', String(nextVal));
            } catch (_) {}
            return nextVal;
        });
    };

    // Validation error states
    const [editFormErrors, setEditFormErrors] = useState({});
    const [loginError, setLoginError] = useState('');

    const handleSessionExpired = (message = 'Session expired or service restarted. Please log in again.') => {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('token');
        localStorage.removeItem('telegram_user');
        localStorage.removeItem('telegramUser');
        setAdminToken('');
        setIsAdmin(false);
        setCurrentUser(null);
        setDomains([]);
        setUsers([]);
        setActions([]);
        setActivityLogs([]);
        setLoginError(message);
        window.dispatchEvent(new Event('domainer_auth_change'));
        window.dispatchEvent(new Event('authChange'));
    };

    const checkAuthStatus = (response) => {
        if (response && (response.status === 401 || response.status === 403)) {
            handleSessionExpired('Your session has expired. Please log in again.');
            return false;
        }
        return true;
    };

    // Helper to strictly require a valid active session before executing any admin action
    const requireActiveToken = () => {
        const storedToken = localStorage.getItem('adminToken') || localStorage.getItem('token');
        if (!storedToken || !adminToken || !isAdmin) {
            handleSessionExpired('Action blocked: Your session has ended. Please log in again.');
            return null;
        }
        return storedToken;
    };

    // Listen for auth change events (e.g. 10-minute inactivity auto-logout, logout from Navbar or another tab)
    useEffect(() => {
        const handleAuthUpdate = () => {
            const storedToken = localStorage.getItem('adminToken') || localStorage.getItem('token');
            if (!storedToken && isAdmin) {
                handleSessionExpired('Session closed. Please log in again.');
            }
        };

        window.addEventListener('authChange', handleAuthUpdate);
        window.addEventListener('domainer_auth_change', handleAuthUpdate);
        window.addEventListener('storage', handleAuthUpdate);
        return () => {
            window.removeEventListener('authChange', handleAuthUpdate);
            window.removeEventListener('domainer_auth_change', handleAuthUpdate);
            window.removeEventListener('storage', handleAuthUpdate);
        };
    }, [isAdmin]);

    // Periodic and on-focus session health verification with backend
    useEffect(() => {
        if (!adminToken || !isAdmin) return;

        const verifySessionWithServer = async () => {
            const currentToken = localStorage.getItem('adminToken') || localStorage.getItem('token');
            if (!currentToken) {
                handleSessionExpired('Session ended. Please log in.');
                return;
            }
            try {
                const response = await fetch('/api/admin/verify-session', {
                    headers: { 'Authorization': `Bearer ${currentToken}` }
                });
                if (!checkAuthStatus(response)) return;
            } catch (err) {
                console.warn('Session verification heartbeat failed:', err);
            }
        };

        window.addEventListener('focus', verifySessionWithServer);
        const heartbeatInterval = setInterval(verifySessionWithServer, 20000);

        return () => {
            window.removeEventListener('focus', verifySessionWithServer);
            clearInterval(heartbeatInterval);
        };
    }, [adminToken, isAdmin]);

    const toggleIdSort = () => {
        setSortTerms(prev => ({
            ...prev,
            domains: prev.domains === 'id-desc' ? 'id-asc' : 'id-desc'
        }));
    };

    const handleTriggerCheckNow = async () => {
        if (isCheckingNow) return;
        const activeToken = requireActiveToken();
        if (!activeToken) return;

        setIsCheckingNow(true);
        try {
            const response = await fetch('/api/admin/domains/check-now', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${activeToken}`
                }
            });
            if (!checkAuthStatus(response)) {
                setIsCheckingNow(false);
                return;
            }
            const result = await response.json();
            if (result.success) {
                setTimeout(() => {
                    fetchAdminData(activeToken);
                    setIsCheckingNow(false);
                }, 1200);
            } else {
                alert(result.message || 'Verification could not be completed');
                setIsCheckingNow(false);
            }
        } catch (e) {
            console.error('Failed to trigger check:', e);
            setIsCheckingNow(false);
        }
    };

    const handleSaveCloudflareIp = async (customIp) => {
        const targetIp = (customIp !== undefined ? customIp : cloudflareIp).trim();
        const ipv4Regex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
        if (!ipv4Regex.test(targetIp)) {
            setCfIpSaveMessage('Please enter a valid IPv4 address (e.g. 162.55.47.15)');
            return;
        }

        const activeToken = requireActiveToken();
        if (!activeToken) return;

        setIsSavingCfIp(true);
        setCfIpSaveMessage('');
        try {
            const response = await fetch('/api/admin/cloudflare-ip', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${activeToken}`
                },
                body: JSON.stringify({ ip: targetIp })
            });
            if (!checkAuthStatus(response)) return;
            const data = await response.json();
            if (data.success) {
                setCfIpSaveMessage(`Cloudflare IP updated to ${targetIp} and synced!`);
                fetchAdminData(activeToken);
                setTimeout(() => setCfIpSaveMessage(''), 4000);
            } else {
                setCfIpSaveMessage(`Error: ${data.error || 'Failed to update'}`);
            }
        } catch (err) {
            console.error('Failed to update Cloudflare IP:', err);
            setCfIpSaveMessage('Network error while updating Cloudflare IP');
        } finally {
            setIsSavingCfIp(false);
        }
    };

    const handleManualCloudflareSync = async () => {
        const activeToken = requireActiveToken();
        if (!activeToken) return;

        setIsSyncingCf(true);
        setCfSyncMessage('');
        try {
            const response = await fetch('/api/admin/cloudflare-sync', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${activeToken}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!checkAuthStatus(response)) {
                setIsSyncingCf(false);
                return;
            }
            const data = await response.json();
            if (data.success) {
                const count = data.result?.syncedCount !== undefined ? data.result.syncedCount : 'all';
                setCfSyncMessage(`Successfully synced ${count} domain records to ${data.targetIp}!`);
                fetchAdminData(activeToken);
                setTimeout(() => setCfSyncMessage(''), 5000);
            } else {
                setCfSyncMessage(`Error: ${data.error || 'Failed to sync'}`);
            }
        } catch (err) {
            console.error('Failed to sync Cloudflare records:', err);
            setCfSyncMessage('Network error while syncing Cloudflare');
        } finally {
            setIsSyncingCf(false);
        }
    };

    const handleUpdateDomainStatus = async (domainId, newStatus) => {
        const activeToken = requireActiveToken();
        if (!activeToken) return;

        const parsedStatus = Number(newStatus);
        try {
            const response = await fetch(`/api/admin/domains/${domainId}/status`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${activeToken}`
                },
                body: JSON.stringify({ status: parsedStatus })
            });
            if (!checkAuthStatus(response)) return;
            const result = await response.json();
            if (result.success) {
                setDomains(prev => prev.map(d => d.id === domainId ? { ...d, status: parsedStatus } : d));
            } else {
                alert(result.error || 'Failed to update domain status');
            }
        } catch (err) {
            console.error('Failed to update domain status:', err);
            alert('Network error while updating status');
        }
    };


    // Check if admin is already logged in and validate admin panel access
    useEffect(() => {
        setPageTitle('Admin Panel');
        // Check if we're running on localhost
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        
        // Validate admin panel URL and active session with server
        const validateAdminAccess = async () => {
            try {
                // If in production, validate the secret admin URL first
                if (!isLocalhost) {
                    if (adminUrl) {
                        const response = await fetch(`/api/validate-admin-url/${adminUrl}`);
                        const result = await response.json();
                        if (!result.valid) {
                            setAccessDenied(true);
                            setIsValidating(false);
                            return;
                        }
                        setAccessDenied(false);
                    } else {
                        setAccessDenied(true);
                        setIsValidating(false);
                        return;
                    }
                }

                // Check for existing session token and verify with server
                const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
                if (token) {
                    try {
                        const verifyRes = await fetch('/api/admin/verify-session', {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (verifyRes.ok) {
                            const verifyData = await verifyRes.json();
                            if (verifyData.success) {
                                setAdminToken(token);
                                setIsAdmin(true);
                                const rawUser = localStorage.getItem('telegram_user') || localStorage.getItem('telegramUser');
                                if (rawUser) {
                                    try { setCurrentUser(JSON.parse(rawUser)); } catch (_) {}
                                }
                                fetchAdminData(token);
                            } else {
                                handleSessionExpired('Session invalid. Please log in again.');
                            }
                        } else {
                            handleSessionExpired('Session expired. Please log in again.');
                        }
                    } catch (e) {
                        console.warn('Initial session verification error:', e);
                    }
                }
            } catch (error) {
                console.error('Error validating admin access:', error);
                setAccessDenied(true);
            } finally {
                setIsValidating(false);
            }
        };
        
        validateAdminAccess();
    }, [adminUrl]);

    // Re-fetch admin data on tab change or window focus to ensure fresh database state
    useEffect(() => {
        const token = adminToken || localStorage.getItem("adminToken") || localStorage.getItem("token");
        if (token && isAdmin) {
            fetchAdminData(token);
        }
    }, [activeTab, isAdmin]);



    // Handle Escape key to close any open admin modals
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                if (showEditModal) setShowEditModal(false);
                if (showAddModal) setShowAddModal(false);
                if (showEditActionModal) setShowEditActionModal(false);
                if (showOrderDetailsModal) setShowOrderDetailsModal(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [showEditModal, showAddModal, showEditActionModal, showOrderDetailsModal]);

    const fetchAdminData = async (token) => {
        try {
            // Fetch all domains
            const domainsResponse = await fetch('/api/admin/domains', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!checkAuthStatus(domainsResponse)) return;
            
            if (domainsResponse.ok) {
                const domainsData = await domainsResponse.json();
                setDomains(domainsData);
            } else {
                console.error('Failed to fetch domains');
            }
            
            // Fetch all users
            const usersResponse = await fetch('/api/admin/users', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!checkAuthStatus(usersResponse)) return;
            
            if (usersResponse.ok) {
                const usersData = await usersResponse.json();
                setUsers(usersData);
            } else {
                console.error('Failed to fetch users');
            }
            
            // Fetch all actions
            const actionsResponse = await fetch('/api/admin/actions', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!checkAuthStatus(actionsResponse)) return;
            
            if (actionsResponse.ok) {
                const actionsData = await actionsResponse.json();
                setActions(actionsData);
            } else {
                console.error('Failed to fetch actions');
            }
            
            // Fetch all activity logs
            const activityLogsResponse = await fetch('/api/admin/activity-log', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!checkAuthStatus(activityLogsResponse)) return;
            
            if (activityLogsResponse.ok) {
                const logsData = await activityLogsResponse.json();
                setActivityLogs(logsData);
            } else {
                console.error('Failed to fetch activity logs');
            }

            // Fetch domain watchdog settings (frequency & undelegation threshold)
            const watchdogResponse = await fetch('/api/admin/watchdog-settings', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!checkAuthStatus(watchdogResponse)) return;
            if (watchdogResponse.ok) {
                const watchdogData = await watchdogResponse.json();
                if (watchdogData.intervalMinutes != null) {
                    setWatchdogInterval(watchdogData.intervalMinutes);
                    try { localStorage.setItem("domainer_watchdog_interval", String(watchdogData.intervalMinutes)); } catch (_) {}
                }
                if (watchdogData.undelegateHours != null) {
                    setWatchdogHours(watchdogData.undelegateHours);
                    try { localStorage.setItem("domainer_watchdog_hours", String(watchdogData.undelegateHours)); } catch (_) {}
                }
            }

            // Fetch Cloudflare status & IP setting
            const cfResponse = await fetch('/api/admin/cloudflare-settings', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!checkAuthStatus(cfResponse)) return;
            if (cfResponse.ok) {
                const cfData = await cfResponse.json();
                setCloudflareStatus(cfData);
                setCloudflareConfigured(!!cfData.configured);
                if (cfData.ip) {
                    setCloudflareIp(cfData.ip);
                }
            }

            // Fetch platform & infrastructure system settings for Settings tab
            try {
                const sysResponse = await fetch('/api/admin/system-settings', {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (sysResponse.ok) {
                    const sysData = await sysResponse.json();
                    setSystemSettings(sysData);
                    if (sysData.watchdogIntervalMinutes != null) {
                        setWatchdogInterval(sysData.watchdogIntervalMinutes);
                    }
                    if (sysData.watchdogUndelegateHours != null) {
                        setWatchdogHours(sysData.watchdogUndelegateHours);
                    }
                    if (sysData.minPrices) {
                        setMinPrices({
                            min_price_buy: sysData.minPrices.buy,
                            min_price_rent: sysData.minPrices.rent,
                            min_price_forward: sysData.minPrices.forward
                        });
                    }
                }
            } catch (_) {}

            // Fetch domain submissions for moderation
            const submissionsResponse = await fetch('/api/admin/submissions', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (submissionsResponse.ok) {
                const sData = await submissionsResponse.json();
                setSubmissions(sData);
            }

            // Fetch auth config / min prices using shared cache
            try {
                const cData = await getAuthConfig();
                if (cData && cData.min_prices) setMinPrices(cData.min_prices);
            } catch (_) {}
        } catch (error) {
            console.error('Error fetching admin data:', error);
        }
    };

    const handleApproveSubmission = async (id) => {
        const activeToken = requireActiveToken();
        if (!activeToken) return;

        try {
            const response = await fetch(`/api/admin/submissions/${id}/approve`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${activeToken}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!checkAuthStatus(response)) return;
            const result = await response.json();
            if (result.success) {
                if (result.type === 'description_update') {
                    // Alert is omitted as per requirement: accepted quietly and list refreshes
                } else {
                    const nsInfo = (result.nameServers && result.nameServers.length > 0)
                        ? `\nAssigned NS:\n${result.nameServers.join('\n')}`
                        : '';
                    alert(`Submission approved! Domain added to Cloudflare and catalog.${nsInfo}`);
                }
                fetchAdminData(activeToken);
            } else {
                alert(result.message || result.error || 'Failed to approve submission');
            }
        } catch (err) {
            console.error('Failed to approve submission:', err);
            alert('Network error while approving submission');
        }
    };

    const handleRejectSubmission = async (id) => {
        const activeToken = requireActiveToken();
        if (!activeToken) return;

        const reason = prompt('Specify rejection reason (optional, leave empty for silent rejection):', '');
        if (reason === null) return;
        const cleanReason = reason ? reason.trim() : '';
        try {
            const response = await fetch(`/api/admin/submissions/${id}/reject`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${activeToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ reason: cleanReason })
            });
            if (!checkAuthStatus(response)) return;
            const result = await response.json();
            if (result.success) {
                fetchAdminData(activeToken);
            } else {
                alert(result.message || result.error || 'Failed to reject submission');
            }
        } catch (err) {
            console.error('Failed to reject submission:', err);
            alert('Network error while rejecting submission');
        }
    };

    const handleAdminLogin = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const username = formData.get('username');
        const password = formData.get('password');
        
        try {
            const response = await fetch('/api/admin-login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ username, password }),
            });
            
            const result = await response.json();
            
            if (result.success) {
                localStorage.setItem('adminToken', result.token);
                localStorage.setItem('token', result.token);
                if (result.user) {
                    localStorage.setItem('telegram_user', JSON.stringify(result.user));
                    localStorage.setItem('telegramUser', JSON.stringify(result.user));
                    setCurrentUser(result.user);
                }
                setAdminToken(result.token);
                setIsAdmin(true);
                setLoginError('');
                window.dispatchEvent(new Event('domainer_auth_change'));
                window.dispatchEvent(new Event('authChange'));
                fetchAdminData(result.token);
            } else {
                setLoginError(result.message);
            }
        } catch (error) {
            console.error('Admin login error:', error);
            setLoginError('Login failed. Please try again.');
        }
    };

    const handleAdminLogout = () => {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('token');
        localStorage.removeItem('telegram_user');
        localStorage.removeItem('telegramUser');
        setAdminToken('');
        setIsAdmin(false);
        setCurrentUser(null);
        setDomains([]);
        setUsers([]);
        window.dispatchEvent(new Event('domainer_auth_change'));
        window.dispatchEvent(new Event('authChange'));
    };

    // Validation functions
    const validateDomainName = (name) => {
        if (!name) return 'Domain name is required';
        const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/;
        if (!domainRegex.test(name)) return 'Invalid domain name format';
        return '';
    };

    const validateNumericField = (value, fieldName) => {
        if (!value && value !== 0) return `${fieldName} is required`;
        if (isNaN(value) || value < 0) return `${fieldName} must be a positive number`;
        if (value > 1000000000) return `${fieldName} cannot exceed 1,000,000,000`;
        return '';
    };

    const validateDescription = (description) => {
        if (!description) return 'Description is required';
        if (description.length > 250) return 'Description must be no more than 250 characters';
        // Allow only text, digits, and punctuation marks
        const descriptionRegex = /^[a-zA-Z0-9\s.,;:!?()\-'"@#$%^&*+=[\]{}|\\/<>`~]*$/;
        if (!descriptionRegex.test(description)) return 'Description can only contain letters, digits, spaces, and common punctuation marks';
        return '';
    };

    const handleEditDomain = (domain) => {
        setEditingDomain(domain);
        setOwnerSearchQuery('');
        setEditForm({
            name: domain.name,
            description: domain.description || '',
            enable_buy: Boolean(domain.pricebuy && Number(domain.pricebuy) > 0),
            pricebuy: domain.pricebuy || '',
            discount_buy: domain.discount_buy !== null && domain.discount_buy !== undefined ? domain.discount_buy : '',
            enable_rent: Boolean(domain.pricerent && Number(domain.pricerent) > 0),
            pricerent: domain.pricerent || '',
            discount_rent: domain.discount_rent !== null && domain.discount_rent !== undefined ? domain.discount_rent : '',
            enable_forward: Boolean(domain.pricefw && Number(domain.pricefw) > 0),
            pricefw: domain.pricefw || '',
            discount_fw: domain.discount_fw !== null && domain.discount_fw !== undefined ? domain.discount_fw : '',
            owner_id: domain.owner_id || '',
            status: domain.status !== undefined ? Number(domain.status) : 1
        });
        // Clear previous errors
        setEditFormErrors({});
        setShowEditModal(true);
    };

    const handleUpdateDomain = async () => {
        if (!editingDomain) return;
        
        // Frontend validation
        const errors = {};
        const nameError = validateDomainName(editForm.name);
        const descriptionError = validateDescription(editForm.description);
        if (nameError) errors.name = nameError;
        if (descriptionError) errors.description = descriptionError;

        if (!editForm.enable_buy && !editForm.enable_rent && !editForm.enable_forward) {
            errors.general = 'Please select at least one format: Buy, Rent, or Forwarding';
        }

        // Owner ID is mandatory
        if (!editForm.owner_id || !String(editForm.owner_id).trim()) {
            errors.owner_id = 'Owner ID is required';
        } else {
            const numericOwner = Number(editForm.owner_id);
            if (isNaN(numericOwner) || numericOwner <= 0) {
                errors.owner_id = 'Please enter or select a valid Owner ID';
            }
        }

        if (editForm.enable_buy) {
            const priceBuyError = validateNumericField(editForm.pricebuy, 'Buy price');
            if (priceBuyError) errors.pricebuy = priceBuyError;
            if (editForm.discount_buy !== '' && editForm.discount_buy !== null) {
                const d = Number(editForm.discount_buy);
                if (isNaN(d) || d < 0 || d > 50) errors.discount_buy = 'Discount cannot exceed 50%';
            }
        }
        if (editForm.enable_rent) {
            const priceRentError = validateNumericField(editForm.pricerent, 'Rent price');
            if (priceRentError) errors.pricerent = priceRentError;
            if (editForm.discount_rent !== '' && editForm.discount_rent !== null) {
                const d = Number(editForm.discount_rent);
                if (isNaN(d) || d < 0 || d > 50) errors.discount_rent = 'Discount cannot exceed 50%';
            }
        }
        if (editForm.enable_forward) {
            const priceFwError = validateNumericField(editForm.pricefw, 'Forward price');
            if (priceFwError) errors.pricefw = priceFwError;
            if (editForm.discount_fw !== '' && editForm.discount_fw !== null) {
                const d = Number(editForm.discount_fw);
                if (isNaN(d) || d < 0 || d > 50) errors.discount_fw = 'Discount cannot exceed 50%';
            }
        }
        
        if (Object.keys(errors).length > 0) {
            setEditFormErrors(errors);
            return;
        }
        
        const activeToken = requireActiveToken();
        if (!activeToken) return;

        try {
            const response = await fetch(`/api/admin/domains/${editingDomain.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${activeToken}`
                },
                body: JSON.stringify({
                    name: editForm.name,
                    description: editForm.description,
                    enable_buy: editForm.enable_buy,
                    pricebuy: editForm.enable_buy ? Number(editForm.pricebuy) : null,
                    discount_buy: editForm.enable_buy && editForm.discount_buy !== '' ? Number(editForm.discount_buy) : null,
                    enable_rent: editForm.enable_rent,
                    pricerent: editForm.enable_rent ? Number(editForm.pricerent) : null,
                    discount_rent: editForm.enable_rent && editForm.discount_rent !== '' ? Number(editForm.discount_rent) : null,
                    enable_forward: editForm.enable_forward,
                    pricefw: editForm.enable_forward ? Number(editForm.pricefw) : null,
                    discount_fw: editForm.enable_forward && editForm.discount_fw !== '' ? Number(editForm.discount_fw) : null,
                    owner_id: Number(editForm.owner_id),
                    status: Number(editForm.status)
                })
            });
            if (!checkAuthStatus(response)) return;
            
            if (response.ok) {
                // Close edit modal and refresh domain list
                setShowEditModal(false);
                setEditFormErrors({});
                fetchAdminData(activeToken);
            } else {
                const error = await response.json();
                console.error('Error updating domain:', error.message);
                setEditFormErrors({ general: error.message });
            }
        } catch (error) {
            console.error('Error updating domain:', error);
            setEditFormErrors({ general: 'Error updating domain' });
        }
    };

    const handleDeleteDomain = async (domainId, domainName = '') => {
        const confirmMessage = domainName 
            ? `Are you sure you want to delete domain "${domainName}"?` 
            : 'Are you sure you want to delete this domain?';
        if (!window.confirm(confirmMessage)) {
            return;
        }

        const activeToken = requireActiveToken();
        if (!activeToken) return;
        
        try {
            const response = await fetch(`/api/admin/domains/${domainId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${activeToken}`
                }
            });
            if (!checkAuthStatus(response)) return;
            
            if (response.ok) {
                // Refresh domain list
                fetchAdminData(activeToken);
            } else {
                const error = await response.json();
                console.error('Error deleting domain:', error.message);
                alert(`Error deleting domain: ${error.message}`);
            }
        } catch (error) {
            console.error('Error deleting domain:', error);
            alert('Error deleting domain');
        }
    };

    // Function to toggle domain delegation check_enabled state
    const handleToggleDomainCheck = async (domainId) => {
        const activeToken = requireActiveToken();
        if (!activeToken) return;

        try {
            const response = await fetch(`/api/admin/domains/${domainId}/toggle-check`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${activeToken}`
                }
            });
            if (!checkAuthStatus(response)) return;
            if (response.ok) {
                fetchAdminData(activeToken);
            } else {
                const error = await response.json();
                alert(`Error toggling domain check: ${error.error || error.message}`);
            }
        } catch (error) {
            console.error('Error toggling domain check:', error);
            alert('Error toggling domain check');
        }
    };

    // Function to toggle user access to personal cabinet
    const toggleUserAccess = async (userId, currentStatus) => {
        const activeToken = requireActiveToken();
        if (!activeToken) return;

        try {
            const response = await fetch(`/api/admin/users/${userId}/toggle-access`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${activeToken}`
                }
            });
            if (!checkAuthStatus(response)) return;
            
            if (response.ok) {
                // Refresh user list
                fetchAdminData(activeToken);
            } else {
                const error = await response.json();
                console.error('Error toggling user access:', error.message);
                alert(`Error toggling user access: ${error.message}`);
            }
        } catch (error) {
            console.error('Error toggling user access:', error);
            alert('Error toggling user access');
        }
    };

    // Function to get status label based on status code
    const getStatusLabel = (domain) => {
        if (domain.is_sold > 0) return 'Sold';
        switch (Number(domain.status)) {
            case 2:
                return 'Moderation';
            case 1:
                return 'Parked (Active)';
            case 0:
                return 'Offline';
            case -1:
                return 'Inactive';
            default:
                return 'Unknown';
        }
    };

    // Function to get status color based on status code
    const getStatusColor = (domain) => {
        if (domain.is_sold > 0) return 'bg-red-500/20 text-red-400 border-red-500/30';
        switch (Number(domain.status)) {
            case 2:
                return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
            case 1:
                return 'bg-green-500/20 text-green-400 border-green-500/30';
            case 0:
                return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
            case -1:
                return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
            default:
                return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
        }
    };

    // Function to format timestamp
    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toLocaleString();
    };

    const handleEditActionSubmit = async (e) => {
        e.preventDefault();
        const activeToken = requireActiveToken();
        if (!activeToken) return;

        try {
            const response = await fetch(`/api/admin/actions/${editingAction.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${activeToken}`
                },
                body: JSON.stringify(editActionForm),
            });
            if (!checkAuthStatus(response)) return;
            
            if (response.ok) {
                setShowEditActionModal(false);
                fetchAdminData(activeToken); // Refresh the list
            } else {
                alert('Failed to update action');
            }
        } catch (error) {
            console.error('Error updating action:', error);
            alert('Error updating action');
        }
    };

    // Show loading state while validating access
    if (isValidating) {
        return (
            <div className="fixed inset-0 flex items-center justify-center py-20 px-6 bg-dark-900">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-neutral-400 text-sm">Verifying access...</p>
                </div>
            </div>
        );
    }

    // Show access denied message if admin URL is invalid
    if (accessDenied) {
        return (
            <div className="py-20 px-6 md:px-8 lg:px-16">
                <div className="max-w-md mx-auto">
                    <div className="card p-8 text-center">
                        <div className="w-16 h-16 rounded-2xl bg-primary-500/10 border border-primary-500/20 flex items-center justify-center mx-auto mb-4 text-primary-400">
                            <LockIcon className="w-8 h-8" />
                        </div>
                        <h2 className="text-2xl font-display font-bold text-white mb-4">Access Denied</h2>
                        <p className="text-neutral-400 mb-6">
                            The admin panel URL you're trying to access is invalid or has been disabled.
                        </p>
                        <button 
                            onClick={() => navigate('/')}
                            className="btn btn-primary px-4 py-2 text-sm"
                        >
                            <span className="relative z-10">Back to Home</span>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!isAdmin) {
        return (
            <div className="fixed inset-0 flex items-center justify-center py-20 px-6 md:px-8 lg:px-16 overflow-y-auto">
                <div className="max-w-md w-full mx-auto my-auto">
                    <div className="card p-8">
                        <h2 className="text-2xl font-display font-bold text-white mb-6 text-center">Admin Login</h2>
                        {loginError && (
                            <div className="mb-4 p-3 bg-red-500/20 text-red-300 rounded-lg text-sm">
                                {loginError}
                            </div>
                        )}
                        <form onSubmit={handleAdminLogin} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-neutral-300 mb-1">Username</label>
                                <input 
                                    type="text" 
                                    name="username"
                                    required
                                    className="w-full px-3 py-2 bg-dark-200 border border-dark-300 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                                    placeholder="admin"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-neutral-300 mb-1">Password</label>
                                <input 
                                    type="password" 
                                    name="password"
                                    required
                                    className="w-full px-3 py-2 bg-dark-200 border border-dark-300 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                                    placeholder="admin"
                                />
                            </div>
                            <button 
                                type="submit"
                                className="w-full btn btn-primary px-4 py-2 text-sm"
                            >
                                <span className="relative z-10">Login</span>
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="py-40 px-6 md:px-8 lg:px-16">
            <div className="max-w-6xl mx-auto">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
                    <div>
                        <h1 className="text-3xl font-display font-bold text-white">Admin Panel</h1>
                        <p className="text-neutral-400 mt-1">Manage all domains and users</p>
                    </div>
                    <button 
                        onClick={handleAdminLogout}
                        className="mt-4 md:mt-0 btn btn-ghost px-4 py-2 text-sm"
                    >
                        Logout
                    </button>
                </div>

                {/* Navigation Tabs */}
                <div className="flex border-b border-dark-300 mb-8 overflow-x-auto">
                    <button
                        onClick={() => handleTabChange('domains')}
                        className={`px-4 py-2 font-medium text-sm whitespace-nowrap ${
                            activeTab === 'domains'
                                ? 'text-primary-400 border-b-2 border-primary-400'
                                : 'text-neutral-400 hover:text-white'
                        }`}
                    >
                        All Domains
                    </button>
                    <button
                        onClick={() => handleTabChange('submissions')}
                        className={`px-4 py-2 font-medium text-sm whitespace-nowrap flex items-center gap-2 ${
                            activeTab === 'submissions'
                                ? 'text-primary-400 border-b-2 border-primary-400'
                                : 'text-neutral-400 hover:text-white'
                        }`}
                    >
                        <span>Moderation</span>
                        {submissions.filter(s => s.status === 'pending').length > 0 && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-amber-500 text-black font-bold">
                                {submissions.filter(s => s.status === 'pending').length}
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => handleTabChange('users')}
                        className={`px-4 py-2 font-medium text-sm whitespace-nowrap ${
                            activeTab === 'users'
                                ? 'text-primary-400 border-b-2 border-primary-400'
                                : 'text-neutral-400 hover:text-white'
                        }`}
                    >
                        Users
                    </button>
                    <button
                        onClick={() => handleTabChange('actions')}
                        className={`px-4 py-2 font-medium text-sm whitespace-nowrap ${
                            activeTab === 'actions'
                                ? 'text-primary-400 border-b-2 border-primary-400'
                                : 'text-neutral-400 hover:text-white'
                        }`}
                    >
                        Active Orders
                    </button>
                    <button
                        onClick={() => handleTabChange('activity')}
                        className={`px-4 py-2 font-medium text-sm whitespace-nowrap ${
                            activeTab === 'activity'
                                ? 'text-primary-400 border-b-2 border-primary-400'
                                : 'text-neutral-400 hover:text-white'
                        }`}
                    >
                        Activity Log
                    </button>
                    <button
                        onClick={() => handleTabChange('settings')}
                        className={`px-4 py-2 font-medium text-sm whitespace-nowrap flex items-center gap-2 ${
                            activeTab === 'settings'
                                ? 'text-primary-400 border-b-2 border-primary-400'
                                : 'text-neutral-400 hover:text-white'
                        }`}
                    >
                        <SettingsIcon className="w-4 h-4" />
                        <span>Settings</span>
                    </button>
                </div>

                {/* Submissions Tab (Moderation) */}
                {activeTab === 'submissions' && (
                    <div className="space-y-4 mb-8">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                            <div>
                                <h3 className="text-lg font-display font-bold text-white">Moderation</h3>
                                <p className="text-xs text-neutral-400 mt-1">
                                    Review new domain listings and description updates. New domains get Cloudflare NS; description updates apply immediately.
                                </p>
                            </div>
                            <span className="text-xs text-neutral-400 bg-dark-200 px-3 py-1 rounded-full border border-dark-300">
                                {submissions.length} submissions
                            </span>
                        </div>

                        {/* Search and Filters for Moderation */}
                        <AdminTableFilterBar
                            searchTerm={searchTerms.submissions}
                            setSearchTerm={(val) => handleSearchChange('submissions', val)}
                            sortBy={sortTerms.submissions}
                            setSortBy={(val) => handleSortChange('submissions', val)}
                            sortOptions={[
                                { value: 'date-desc', label: 'Date: Newest first' },
                                { value: 'date-asc', label: 'Date: Oldest first' },
                                { value: 'pending-first', label: 'Pending first' },
                                { value: 'domain-az', label: 'Domain: A → Z' }
                            ]}
                            resultsCount={filteredSubmissions.length}
                            totalCount={submissions.length}
                            itemLabel="submissions"
                            placeholder="Search moderation requests by domain, user, description, status, NS..."
                            onReset={() => handleResetTab('submissions', 'date-desc')}
                        />

                        {/* Top Pagination for seamless switching without scrolling */}
                        <TablePagination
                            currentPage={currentPage.submissions}
                            totalItems={filteredSubmissions.length}
                            pageSize={100}
                            onPageChange={(page) => handlePageChange('submissions', page)}
                            position="top"
                        />

                        <div className="domainer-table-container my-2">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-white/10 bg-white/[0.02]">
                                            <th className="text-left py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">ID / Date</th>
                                            <th className="text-left py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Domain & Description</th>
                                            <th className="text-left py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">User</th>
                                            <th className="text-left py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Options / Prices</th>
                                            <th className="text-center py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Status</th>
                                            <th className="text-center py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                <tbody>
                                    {filteredSubmissions.length === 0 ? (
                                        <AdminTableEmptyState
                                            colSpan={6}
                                            searchTerm={searchTerms.submissions}
                                            itemLabel="submissions"
                                            emptyMessage="No moderation requests found"
                                            onReset={() => handleResetTab('submissions', 'date-desc')}
                                        />
                                    ) : (
                                        paginatedSubmissions.map((sub) => {
                                            let tg = null;
                                            try {
                                                if (sub.telegram_user) tg = JSON.parse(sub.telegram_user);
                                            } catch (e) {}

                                            let nsList = [];
                                            try {
                                                if (sub.assigned_ns) nsList = JSON.parse(sub.assigned_ns);
                                            } catch (e) {}

                                            return (
                                                <tr key={sub.id} className="border-b border-dark-300/50 hover:bg-dark-200/30">
                                                    <td className="py-3 px-4 text-xs text-neutral-400 whitespace-nowrap">
                                                        <p className="font-mono text-white">#{sub.id}</p>
                                                        <p>{new Date(sub.created_at).toLocaleString()}</p>
                                                        <span className={`inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${
                                                            sub.type === 'description_update' 
                                                                ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                                                                : 'bg-primary-500/20 text-primary-300 border border-primary-500/30'
                                                        }`}>
                                                            {sub.type === 'description_update' ? '📝 Description' : '🌐 New Domain'}
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-4">
                                                        <p className="text-white font-bold">{sub.domain_name}</p>
                                                        {sub.type === 'description_update' ? (
                                                            <div className="mt-1 space-y-1.5 max-w-md">
                                                                <div className="text-[11px] text-neutral-400 bg-dark-300/40 p-2 rounded-lg border border-dark-300/60">
                                                                    <span className="text-neutral-500 block text-[10px] font-bold uppercase tracking-wider mb-0.5">Current Description:</span>
                                                                    <span className="line-through opacity-80">{sub.current_description || '—'}</span>
                                                                </div>
                                                                <div className="text-xs text-emerald-300 bg-emerald-500/10 p-2 rounded-lg border border-emerald-500/30 font-medium">
                                                                    <span className="text-emerald-400 block text-[10px] font-bold uppercase tracking-wider mb-0.5">Proposed Description:</span>
                                                                    <span>{sub.description}</span>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <p className="text-xs text-neutral-400 max-w-xs">{sub.description}</p>
                                                                {nsList.length > 0 && (
                                                                    <div className="mt-1 flex flex-wrap gap-1">
                                                                        {nsList.map((ns, i) => (
                                                                            <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-primary-500/20 text-primary-300 font-mono">
                                                                                {ns}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                    </td>
                                                    <td className="py-3 px-4 text-xs">
                                                        <p className="text-white font-medium">
                                                            {tg ? `${tg.first_name || ''} ${tg.last_name || ''}` : 'User'}
                                                        </p>
                                                        {tg?.username && <p className="text-neutral-400">@{tg.username}</p>}
                                                        <p className="text-neutral-500 font-mono text-[11px]">ID: {sub.telegram_user_id || sub.user_id}</p>
                                                    </td>
                                                    <td className="py-3 px-4 text-xs space-y-1">
                                                        {sub.type === 'description_update' ? (
                                                            <span className="text-xs text-neutral-400 italic">Text update</span>
                                                        ) : (
                                                            <>
                                                                {sub.enable_buy === 1 && (
                                                                    <span className="inline-block px-2 py-0.5 rounded bg-primary-500/20 text-primary-300 mr-1 mb-1">
                                                                        Buy: ${sub.pricebuy?.toLocaleString()} {sub.discount_buy > 0 ? `(-${sub.discount_buy}%)` : ''}
                                                                    </span>
                                                                )}
                                                                {sub.enable_rent === 1 && (
                                                                    <span className="inline-block px-2 py-0.5 rounded bg-secondary-500/20 text-secondary-300 mr-1 mb-1">
                                                                        Rent: ${sub.pricerent}/mo {sub.discount_rent > 0 ? `(-${sub.discount_rent}%)` : ''}
                                                                    </span>
                                                                )}
                                                                {sub.enable_forward === 1 && (
                                                                    <span className="inline-block px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 mr-1 mb-1">
                                                                        FW: ${sub.pricefw}/day {sub.discount_fw > 0 ? `(-${sub.discount_fw}%)` : ''}
                                                                    </span>
                                                                )}
                                                            </>
                                                        )}
                                                    </td>
                                                    <td className="py-3 px-4 text-center">
                                                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                                                            sub.status === 'approved'
                                                                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                                                : sub.status === 'rejected'
                                                                    ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                                                                    : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                                                        }`}>
                                                            {sub.status === 'approved' && 'Approved'}
                                                            {sub.status === 'rejected' && 'Rejected'}
                                                            {sub.status === 'pending' && 'Pending'}
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-4 text-center">
                                                        {sub.status === 'pending' ? (
                                                            <div className="flex items-center justify-center gap-2">
                                                                <button
                                                                    onClick={() => handleApproveSubmission(sub.id)}
                                                                    className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold shadow-md transition-all cursor-pointer flex items-center gap-1.5"
                                                                >
                                                                    <CheckIcon className="w-3.5 h-3.5" />
                                                                    <span>Approve</span>
                                                                </button>
                                                                <button
                                                                    onClick={() => handleRejectSubmission(sub.id)}
                                                                    className="px-3 py-1.5 rounded-lg bg-red-600/30 hover:bg-red-600 text-red-300 hover:text-white text-xs font-medium border border-red-500/40 transition-all cursor-pointer flex items-center gap-1.5"
                                                                >
                                                                    <CrossIcon className="w-3.5 h-3.5" />
                                                                    <span>Reject</span>
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <span className="text-xs text-neutral-500">
                                                                {sub.status === 'approved' ? 'Processed' : sub.rejection_reason || 'Rejected'}
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                            </div>
                        </div>
                        <TablePagination
                            currentPage={currentPage.submissions}
                            totalItems={filteredSubmissions.length}
                            pageSize={100}
                            onPageChange={(page) => handlePageChange('submissions', page)}
                        />
                    </div>
                )}

                {/* All Domains Tab */}
                {activeTab === 'domains' && (
                    <div className="space-y-4 mb-8">
                        {/* Header bar with title, Add Domain button and quick Check Now action */}
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                            <div className="flex items-center gap-3">
                                <h3 className="text-lg font-display font-bold text-white">All Domains</h3>
                                <span className="text-xs text-neutral-400 bg-dark-200 px-2.5 py-1 rounded-full border border-dark-300">
                                    {domains.length} total
                                </span>
                            </div>

                            <div className="flex items-center gap-2.5 w-full sm:w-auto justify-end flex-wrap">
                                <button
                                    onClick={handleTriggerCheckNow}
                                    disabled={isCheckingNow}
                                    className="px-3.5 py-2 rounded-xl text-xs font-semibold bg-dark-200 hover:bg-dark-300 disabled:opacity-50 text-neutral-200 border border-dark-300 transition-all flex items-center gap-2 select-none cursor-pointer"
                                    title="Trigger an immediate unscheduled verification for all domains"
                                >
                                    <RefreshIcon className={`w-3.5 h-3.5 ${isCheckingNow ? 'animate-spin text-primary-400' : 'text-neutral-400'}`} />
                                    <span>{isCheckingNow ? 'Verifying all domains...' : 'Check Now'}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleTabChange('settings')}
                                    className="px-3.5 py-2 rounded-xl text-xs font-semibold bg-dark-200 hover:bg-dark-300 text-neutral-300 hover:text-white border border-dark-300 transition-all flex items-center gap-1.5 cursor-pointer"
                                    title="Open System & Infrastructure Settings"
                                >
                                    <SettingsIcon className="w-3.5 h-3.5 text-primary-400" />
                                    <span>Settings</span>
                                </button>
                                <button 
                                    onClick={() => setShowAddModal(true)}
                                    className="btn btn-primary px-4 py-2 text-sm whitespace-nowrap"
                                >
                                    <span className="relative z-10">Add Domain</span>
                                </button>
                            </div>
                        </div>

                        {/* Search and Filters for Domains */}
                        <AdminTableFilterBar
                            searchTerm={searchTerms.domains}
                            setSearchTerm={(val) => handleSearchChange('domains', val)}
                            sortBy={sortTerms.domains}
                            setSortBy={(val) => handleSortChange('domains', val)}
                            sortOptions={[
                                { value: 'id-desc', label: 'ID: 9 → 1 (Newest)' },
                                { value: 'id-asc', label: 'ID: 1 → 9 (Oldest)' },
                                { value: 'name-az', label: 'Name: A → Z' },
                                { value: 'name-za', label: 'Name: Z → A' },
                                { value: 'price-high', label: 'Buy Price: High to Low' },
                                { value: 'price-low', label: 'Buy Price: Low to High' },
                                { value: 'status', label: 'Status: Active first' }
                            ]}
                            resultsCount={filteredDomains.length}
                            totalCount={domains.length}
                            itemLabel="domains"
                            placeholder="Search domains by name, description, owner, price, status, IP..."
                            onReset={() => handleResetTab('domains', 'id-desc')}
                        />

                        {/* Top Pagination for seamless switching without scrolling */}
                        <TablePagination
                            currentPage={currentPage.domains}
                            totalItems={filteredDomains.length}
                            pageSize={100}
                            onPageChange={(page) => handlePageChange('domains', page)}
                            position="top"
                        />

                        {/* Domains List with Status Display */}
                        <div className="domainer-table-container my-2">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-white/10 bg-white/[0.02]">
                                        <th 
                                            onClick={toggleIdSort}
                                            className="text-left py-3 px-4 text-sm font-semibold text-neutral-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors select-none group"
                                            title="Click to toggle sorting by ID"
                                        >
                                            <div className="flex items-center gap-1.5">
                                                <span>Domain</span>
                                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono font-medium transition-colors ${
                                                    sortTerms.domains.startsWith('id') 
                                                        ? 'bg-primary-500/20 text-primary-300 border border-primary-500/30' 
                                                        : 'bg-dark-300/40 text-neutral-400 group-hover:text-white'
                                                }`}>
                                                    {sortTerms.domains === 'id-asc' ? '#ID 1→9 ▲' : '#ID 9→1 ▼'}
                                                </span>
                                            </div>
                                        </th>
                                        <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-400 uppercase tracking-wider">Owner</th>
                                        <th className="text-left py-3 px-4 text-sm font-semibold text-neutral-400 uppercase tracking-wider">Pricing</th>
                                        <th className="text-center py-3 px-4 text-sm font-semibold text-neutral-400 uppercase tracking-wider">Status</th>
                                        <th className="text-center py-3 px-4 text-sm font-semibold text-neutral-400 uppercase tracking-wider">
                                            <div className="flex flex-col items-center">
                                                <span className="inline-flex items-center gap-1.5">
                                                    <DogIcon className="w-3.5 h-3.5 text-primary-400" />
                                                    <span>Watchdog</span>
                                                </span>
                                                <span className="text-[10px] text-neutral-500 font-normal lowercase tracking-normal font-mono">(auto check)</span>
                                            </div>
                                        </th>
                                        <th className="text-center py-3 px-4 text-sm font-semibold text-neutral-400 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredDomains.length === 0 ? (
                                        <AdminTableEmptyState
                                            colSpan={6}
                                            searchTerm={searchTerms.domains}
                                            itemLabel="domains"
                                            emptyMessage="No domains found"
                                            onReset={() => handleResetTab('domains', 'id-desc')}
                                        />
                                    ) : (
                                        paginatedDomains.map((domain) => (
                                        <tr key={domain.id} className="border-b border-dark-300/50 hover:bg-dark-200/30">
                                            <td className="py-3 px-4">
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs text-primary-400 font-mono font-semibold">#{domain.id}</span>
                                                        <p className="text-white font-medium">{domain.name}</p>
                                                    </div>
                                                    <p className="text-neutral-400 text-sm mt-0.5">{domain.description}</p>
                                                </div>
                                            </td>
                                            <td className="py-3 px-4">
                                                <div>
                                                    {domain.owner_id ? (
                                                        <div>
                                                            <p className="text-white text-sm">
                                                                {domain.owner_first_name} {domain.owner_last_name}
                                                            </p>
                                                            {domain.owner_username && (
                                                                <p className="text-neutral-400 text-xs">@{domain.owner_username}</p>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <p className="text-neutral-400 text-sm">No owner</p>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="py-3 px-4 text-xs space-y-1">
                                                {domain.pricebuy && Number(domain.pricebuy) > 0 && (
                                                    <span className="inline-block px-2 py-0.5 rounded bg-primary-500/20 text-primary-300 mr-1 mb-1 font-medium whitespace-nowrap">
                                                        Buy: ${Number(domain.pricebuy).toLocaleString()} {domain.discount_buy > 0 ? `(-${domain.discount_buy}%)` : ''}
                                                    </span>
                                                )}
                                                {domain.pricerent && Number(domain.pricerent) > 0 && (
                                                    <span className="inline-block px-2 py-0.5 rounded bg-secondary-500/20 text-secondary-300 mr-1 mb-1 font-medium whitespace-nowrap">
                                                        Rent: ${Number(domain.pricerent).toLocaleString()}/mo {domain.discount_rent > 0 ? `(-${domain.discount_rent}%)` : ''}
                                                    </span>
                                                )}
                                                {domain.pricefw && Number(domain.pricefw) > 0 && (
                                                    <span className="inline-block px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 mr-1 mb-1 font-medium whitespace-nowrap">
                                                        FW: ${Number(domain.pricefw).toLocaleString()}/day {domain.discount_fw > 0 ? `(-${domain.discount_fw}%)` : ''}
                                                    </span>
                                                )}
                                                {(!domain.pricebuy || Number(domain.pricebuy) <= 0) && (!domain.pricerent || Number(domain.pricerent) <= 0) && (!domain.pricefw || Number(domain.pricefw) <= 0) && (
                                                    <span className="text-neutral-500">—</span>
                                                )}
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <div className="flex flex-col items-center gap-1">
                                                    {domain.is_sold > 0 ? (
                                                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-red-500/20 text-red-400 border border-red-500/30">
                                                            Sold
                                                        </span>
                                                    ) : Number(domain.status) === 2 ? (
                                                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/30 flex items-center justify-center gap-1 shadow-sm" title="This domain is currently under moderation">
                                                            <span>⏳</span>
                                                            <span>Moderation</span>
                                                        </span>
                                                    ) : (
                                                        <select
                                                            value={domain.status}
                                                            onChange={(e) => handleUpdateDomainStatus(domain.id, e.target.value)}
                                                            className={`px-2 py-1 rounded-full text-xs font-semibold cursor-pointer border transition-colors outline-none text-center bg-dark-200 hover:bg-dark-300 ${getStatusColor(domain)}`}
                                                            title={`Current status: ${getStatusLabel(domain)}. Click to change.`}
                                                        >
                                                            <option value={1} className="bg-dark-100 text-green-400">Parked (Active)</option>
                                                            <option value={0} className="bg-dark-100 text-amber-400">Offline</option>
                                                            <option value={-1} className="bg-dark-100 text-gray-400">Inactive</option>
                                                        </select>
                                                    )}
                                                    {(() => {
                                                        const now = new Date();
                                                        const rentUntil = domain.rent_until ? new Date(domain.rent_until) : null;
                                                        const forwardUntil = domain.forward_until ? new Date(domain.forward_until) : null;
                                                        
                                                        if (domain.is_sold > 0) return <span className="text-[10px] text-red-400 font-semibold uppercase tracking-wider">Sold</span>;
                                                        if (rentUntil && rentUntil > now) {
                                                             const onClick = () => {
                                                                 const action = actions.find(a => a.domain_id === domain.id && a.action_type === 'rent' && new Date(a.end_time).getTime() === rentUntil.getTime());
                                                                 if (action) {
                                                                     setEditingAction(action);
                                                                     setEditActionForm({ destination: action.destination || '', end_time: action.end_time.substring(0, 16) });
                                                                     setShowEditActionModal(true);
                                                                 } else {
                                                                     alert("Order details not found. Try refreshing the page.");
                                                                 }
                                                             };
                                                             return <span onClick={onClick} className="text-[10px] text-orange-400 font-semibold uppercase tracking-wider cursor-pointer hover:underline" title="Click to edit order">Rented until {rentUntil.toLocaleDateString()}</span>;
                                                         }
                                                         if (forwardUntil && forwardUntil > now) {
                                                             const onClick = () => {
                                                                 const action = actions.find(a => a.domain_id === domain.id && a.action_type === 'forward' && new Date(a.end_time).getTime() === forwardUntil.getTime());
                                                                 if (action) {
                                                                     setEditingAction(action);
                                                                     setEditActionForm({ destination: action.destination || '', end_time: action.end_time.substring(0, 16) });
                                                                     setShowEditActionModal(true);
                                                                 } else {
                                                                     alert("Order details not found. Try refreshing the page.");
                                                                 }
                                                             };
                                                             return <span onClick={onClick} className="text-[10px] text-emerald-400 font-semibold uppercase tracking-wider cursor-pointer hover:underline" title="Click to edit order">Forwarded until {forwardUntil.toLocaleDateString()}</span>;
                                                         }
                                                         return null;
                                                     })()}
                                                 </div>
                                             </td>

                                             {/* Unified Watchdog (top toggle) & Last Checked (bottom date) Column */}
                                             <td className="py-3 px-4 text-center">
                                                 <div className="flex flex-col items-center justify-center gap-1.5">
                                                     {/* Top: Watchdog toggle */}
                                                     {(() => {
                                                         const isEligible = (Number(domain.status) === 1 || Number(domain.status) === 0) && !(domain.is_sold > 0);
                                                         if (!isEligible) {
                                                             return (
                                                                 <div className="flex items-center gap-1.5">
                                                                     <DogIcon className="w-3.5 h-3.5 text-neutral-500 opacity-50 inline-block" title="Watchdog (disabled)" />
                                                                     <button 
                                                                         type="button"
                                                                         disabled={true}
                                                                         className="relative inline-flex h-5 w-9 shrink-0 cursor-not-allowed rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none bg-dark-400/50 opacity-40"
                                                                         title={
                                                                             Number(domain.status) === 2 
                                                                                 ? "Watchdog is disabled for domains under moderation" 
                                                                                 : domain.is_sold > 0
                                                                                 ? "Watchdog is disabled for sold domains"
                                                                                 : "Watchdog toggle is disabled for this domain"
                                                                         }
                                                                     >
                                                                         <span className="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-neutral-400 shadow ring-0 transition duration-200 ease-in-out translate-x-0" />
                                                                     </button>
                                                                     <span className="text-[10px] text-neutral-500 font-mono">Off</span>
                                                                 </div>
                                                             );
                                                         }
                                                         return (
                                                             <div className="flex items-center gap-1.5">
                                                                 <DogIcon className="w-3.5 h-3.5 text-primary-400 inline-block" title="Watchdog" />
                                                                 <button 
                                                                     type="button"
                                                                     onClick={() => handleToggleDomainCheck(domain.id)}
                                                                     className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                                                         domain.check_enabled === 0 ? 'bg-dark-400' : 'bg-primary-600'
                                                                     }`}
                                                                     title={domain.check_enabled === 0 ? "Automated check disabled. Click to turn ON" : "Automated check active. Click to turn OFF"}
                                                                 >
                                                                     <span
                                                                         className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                                                             domain.check_enabled === 0 ? 'translate-x-0' : 'translate-x-4'
                                                                         }`}
                                                                     />
                                                                 </button>
                                                                 <span className={`text-[10px] font-mono font-medium ${domain.check_enabled === 0 ? 'text-neutral-500' : 'text-primary-400'}`}>
                                                                     {domain.check_enabled === 0 ? 'Off' : 'Active'}
                                                                 </span>
                                                             </div>
                                                         );
                                                     })()}

                                                     {/* Bottom: Last Checked timestamp */}
                                                     {(() => {
                                                         if (!domain.delegation_salt || domain.delegation_salt === 'default_salt') {
                                                             return (
                                                                 <span className="text-[11px] text-neutral-500 font-mono">Not verified</span>
                                                             );
                                                         }
                                                         const checkDate = new Date(domain.delegation_salt);
                                                         if (isNaN(checkDate.getTime())) {
                                                             return (
                                                                 <span className="text-[11px] text-neutral-500 font-mono">Not verified</span>
                                                             );
                                                         }
                                                         return (
                                                             <div className="flex items-center gap-1.5">
                                                                 <span 
                                                                     className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                                                                         domain.check_enabled === 0 
                                                                             ? 'bg-neutral-500' 
                                                                             : domain.status === 1 ? 'bg-emerald-400' : 'bg-amber-400'
                                                                     }`}
                                                                     title={
                                                                         domain.check_enabled === 0 
                                                                             ? 'Check paused' 
                                                                             : domain.status === 1 ? 'Verified & Active' : 'Checked (Pending/Offline)'
                                                                     }
                                                                 ></span>
                                                                 <span className={`text-[11px] font-mono ${domain.check_enabled === 0 ? 'text-neutral-500' : 'text-neutral-300'}`}>
                                                                     {checkDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                                                     <span className="text-neutral-500 mx-1">·</span>
                                                                     {checkDate.toLocaleDateString([], { month: 'short', day: 'numeric' })}
                                                                 </span>
                                                             </div>
                                                         );
                                                     })()}
                                                 </div>
                                             </td>
                                             <td className="py-3 px-4 text-center">
                                                 <button 
                                                     onClick={() => handleEditDomain(domain)}
                                                     className="text-primary-400 hover:text-primary-300 text-sm mr-3 font-medium cursor-pointer"
                                                 >
                                                     Edit
                                                 </button>
                                                 <button 
                                                     onClick={() => handleDeleteDomain(domain.id, domain.name)}
                                                     className="text-red-400 hover:text-red-300 text-sm font-medium cursor-pointer"
                                                 >
                                                     Delete
                                                 </button>
                                             </td>
                                         </tr>
                                     )))}
                                 </tbody>
                            </table>
                            </div>
                        </div>
                        <TablePagination
                            currentPage={currentPage.domains}
                            totalItems={filteredDomains.length}
                            pageSize={100}
                            onPageChange={(page) => handlePageChange('domains', page)}
                        />
                    </div>
                )}

                {/* Users Tab */}
                {activeTab === 'users' && (
                    <div className="space-y-4 mb-8">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-display font-bold text-white">Users</h3>
                            <span className="text-xs text-neutral-400 bg-dark-200 px-2.5 py-1 rounded-full border border-dark-300">
                                {users.length} total
                            </span>
                        </div>

                        {/* Search and Filters for Users */}
                        <AdminTableFilterBar
                            searchTerm={searchTerms.users}
                            setSearchTerm={(val) => handleSearchChange('users', val)}
                            sortBy={sortTerms.users}
                            setSortBy={(val) => handleSortChange('users', val)}
                            sortOptions={[
                                { value: 'date-desc', label: 'Registered: Newest first' },
                                { value: 'date-asc', label: 'Registered: Oldest first' },
                                { value: 'name-az', label: 'Name: A → Z' },
                                { value: 'last-active', label: 'Last Active: Recent first' },
                                { value: 'active-first', label: 'Active users first' }
                            ]}
                            resultsCount={filteredUsers.length}
                            totalCount={users.length}
                            itemLabel="users"
                            placeholder="Search users by name, username, Telegram ID, status..."
                            onReset={() => handleResetTab('users', 'date-desc')}
                        />

                        {/* Top Pagination for seamless switching without scrolling */}
                        <TablePagination
                            currentPage={currentPage.users}
                            totalItems={filteredUsers.length}
                            pageSize={100}
                            onPageChange={(page) => handlePageChange('users', page)}
                            position="top"
                        />

                        <div className="domainer-table-container my-2">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-white/10 bg-white/[0.02]">
                                            <th className="text-left py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">ID</th>
                                            <th className="text-left py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">User</th>
                                            <th className="text-left py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Telegram ID</th>
                                            <th className="text-left py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Registration Date</th>
                                            <th className="text-left py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Last Activity</th>
                                            <th className="text-center py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Cabinet Access</th>
                                        </tr>
                                    </thead>
                                <tbody>
                                    {filteredUsers.length === 0 ? (
                                        <AdminTableEmptyState
                                            colSpan={6}
                                            searchTerm={searchTerms.users}
                                            itemLabel="users"
                                            emptyMessage="No users found"
                                            onReset={() => handleResetTab('users', 'date-desc')}
                                        />
                                    ) : (
                                        paginatedUsers.map((user) => (
                                        <tr key={user.id} className="border-b border-dark-300/50 hover:bg-dark-200/30">
                                            <td className="py-3 px-4">
                                                <span className="text-xs text-primary-400 font-mono font-bold">#{user.id}</span>
                                            </td>
                                            <td className="py-3 px-4">
                                                <div className="flex items-center">
                                                    <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-sky-500/20 to-purple-500/20 border border-sky-500/30 text-sky-400 font-bold text-xs flex items-center justify-center mr-3 select-none flex-shrink-0">
                                                        {user.first_name ? user.first_name[0].toUpperCase() : (user.username ? user.username[0].toUpperCase() : '#')}
                                                    </div>
                                                    <div>
                                                        <p className="text-white font-medium">
                                                            {user.first_name} {user.last_name}
                                                        </p>
                                                        {user.username && (
                                                            <p className="text-neutral-400 text-sm">@{user.username}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-3 px-4 text-neutral-400 font-mono text-xs">{user.telegram_id}</td>
                                            <td className="py-3 px-4 text-neutral-400 text-xs">{formatTimestamp(user.registered_time)}</td>
                                            <td className="py-3 px-4 text-neutral-400 text-xs">{formatTimestamp(user.last_time)}</td>
                                            <td className="py-3 px-4 text-center">
                                                <div className="flex flex-col items-center justify-center gap-1">
                                                    <button 
                                                        type="button"
                                                        onClick={() => toggleUserAccess(user.id, user.disabled)}
                                                        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                                            user.disabled === 1 ? 'bg-dark-400' : 'bg-emerald-600'
                                                        }`}
                                                        title={user.disabled === 1 ? "Access blocked. Click to enable access" : "Access active. Click to block access"}
                                                    >
                                                        <span
                                                            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                                                user.disabled === 1 ? 'translate-x-0' : 'translate-x-4'
                                                            }`}
                                                        />
                                                    </button>
                                                    <span className={`text-[10px] font-mono font-medium ${user.disabled === 1 ? 'text-red-400' : 'text-emerald-400'}`}>
                                                        {user.disabled === 1 ? 'Blocked' : 'Active'}
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                    )))}
                                </tbody>
                            </table>
                            </div>
                        </div>
                        <TablePagination
                            currentPage={currentPage.users}
                            totalItems={filteredUsers.length}
                            pageSize={100}
                            onPageChange={(page) => handlePageChange('users', page)}
                        />
                    </div>
                )}

                {/* Active Orders Tab */}
                {activeTab === 'actions' && (
                    <div className="space-y-4 mb-8">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-semibold text-white">Active Orders</h2>
                            <span className="text-xs text-neutral-400 bg-dark-200 px-2.5 py-1 rounded-full border border-dark-300">
                                {actions.length} total
                            </span>
                        </div>

                        {/* Search and Filters for Active Orders */}
                        <AdminTableFilterBar
                            searchTerm={searchTerms.actions}
                            setSearchTerm={(val) => handleSearchChange('actions', val)}
                            sortBy={sortTerms.actions}
                            setSortBy={(val) => handleSortChange('actions', val)}
                            sortOptions={[
                                { value: 'date-desc', label: 'Time: Newest first' },
                                { value: 'date-asc', label: 'Time: Oldest first' },
                                { value: 'amount-high', label: 'Paid: High to Low' },
                                { value: 'domain-az', label: 'Domain: A → Z' }
                            ]}
                            resultsCount={filteredActions.length}
                            totalCount={actions.length}
                            itemLabel="orders"
                            placeholder="Search orders by domain, user, action (buy/rent/forward), destination, price..."
                            onReset={() => handleResetTab('actions', 'date-desc')}
                        />

                        {/* Top Pagination for seamless switching without scrolling */}
                        <TablePagination
                            currentPage={currentPage.actions}
                            totalItems={filteredActions.length}
                            pageSize={100}
                            onPageChange={(page) => handlePageChange('actions', page)}
                            position="top"
                        />

                        <div className="domainer-table-container my-2">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="border-b border-white/10 bg-white/[0.02]">
                                            <th className="text-left py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Time</th>
                                            <th className="text-left py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">User</th>
                                            <th className="text-left py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Domain</th>
                                            <th className="text-left py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Action</th>
                                            <th className="text-left py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Duration</th>
                                            <th className="text-left py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Paid</th>
                                            <th className="text-left py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Destination</th>
                                            <th className="text-left py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Ends</th>
                                            <th className="text-center py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Controls</th>
                                        </tr>
                                    </thead>
                                <tbody>
                                    {filteredActions.length === 0 ? (
                                        <AdminTableEmptyState
                                            colSpan={9}
                                            searchTerm={searchTerms.actions}
                                            itemLabel="orders"
                                            emptyMessage="No orders recorded yet"
                                            onReset={() => handleResetTab('actions', 'date-desc')}
                                        />
                                    ) : (
                                        paginatedActions.map((action) => {
                                            const isPast = (action.end_time && new Date(action.end_time) < new Date()) || action.action_type === 'buy';
                                            return (
                                            <tr key={action.id} className={`border-b border-dark-300/50 hover:bg-dark-200/30 ${isPast ? 'opacity-50 grayscale' : ''}`}>
                                                <td className="py-3 px-4 text-neutral-400 whitespace-nowrap">
                                                    {formatTimestamp(action.start_time)}
                                                </td>
                                                <td className="py-3 px-4">
                                                    <div className="text-white">
                                                        {action.first_name} {action.last_name}
                                                    </div>
                                                    {action.username && (
                                                        <div className="text-xs text-neutral-400">@{action.username}</div>
                                                    )}
                                                </td>
                                                <td className="py-3 px-4 font-medium text-primary-400">
                                                    {action.domain_name}
                                                </td>
                                                <td className="py-3 px-4">
                                                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                                        action.action_type === 'buy' ? 'bg-blue-500/20 text-blue-400' :
                                                        action.action_type === 'rent' ? 'bg-purple-500/20 text-purple-400' :
                                                        'bg-emerald-500/20 text-emerald-400'
                                                    }`}>
                                                        {action.action_type.toUpperCase()}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4 text-neutral-400">
                                                    {action.duration ? `${action.duration} ${action.action_type === 'rent' ? 'mo' : 'days'}` : '-'}
                                                </td>
                                                <td className="py-3 px-4">
                                                    <button
                                                        onClick={() => {
                                                            setSelectedOrderDetails(action);
                                                            setShowOrderDetailsModal(true);
                                                        }}
                                                        className="group inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 hover:border-emerald-500/50 text-emerald-400 font-semibold transition-all hover:scale-105 active:scale-95 cursor-pointer shadow-sm text-sm"
                                                        title="Click to view detailed invoice & payment receipt"
                                                    >
                                                        <span>${action.price_paid}</span>
                                                        <svg className="w-3.5 h-3.5 text-emerald-300 opacity-70 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                        </svg>
                                                    </button>
                                                </td>
                                                <td className="py-3 px-4 text-neutral-300 max-w-[150px] truncate" title={action.destination}>
                                                    {action.destination || '-'}
                                                </td>
                                                <td className="py-3 px-4 text-neutral-400 whitespace-nowrap">
                                                    {action.end_time ? formatTimestamp(action.end_time) : '-'}
                                                </td>
                                                <td className="py-3 px-4 text-center">
                                                    <button 
                                                        onClick={() => {
                                                            setEditingAction(action);
                                                            setEditActionForm({ 
                                                                destination: action.destination || '', 
                                                                end_time: action.end_time ? action.end_time.substring(0, 16) : '' // format for datetime-local
                                                            });
                                                            setShowEditActionModal(true);
                                                        }}
                                                        className="text-primary-400 hover:text-primary-300 mr-3"
                                                        title="Edit Action"
                                                    >
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                        </svg>
                                                    </button>
                                                </td>
                                            </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                            </div>
                        </div>
                        <TablePagination
                            currentPage={currentPage.actions}
                            totalItems={filteredActions.length}
                            pageSize={100}
                            onPageChange={(page) => handlePageChange('actions', page)}
                        />
                    </div>
                )}

                {/* Activity Log Tab */}
                {activeTab === 'activity' && (
                    <div className="space-y-4 mb-8">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-semibold text-white">Activity Log</h2>
                            <span className="text-xs text-neutral-400 bg-dark-200 px-2.5 py-1 rounded-full border border-dark-300">
                                {activityLogs.length} total
                            </span>
                        </div>

                        {/* Search and Filters for Activity Log */}
                        <AdminTableFilterBar
                            searchTerm={searchTerms.activity}
                            setSearchTerm={(val) => handleSearchChange('activity', val)}
                            sortBy={sortTerms.activity}
                            setSortBy={(val) => handleSortChange('activity', val)}
                            sortOptions={[
                                { value: 'date-desc', label: 'Time: Newest first' },
                                { value: 'date-asc', label: 'Time: Oldest first' },
                                { value: 'user-az', label: 'User: A → Z' },
                                { value: 'action-az', label: 'Action: A → Z' }
                            ]}
                            resultsCount={filteredActivity.length}
                            totalCount={activityLogs.length}
                            itemLabel="log entries"
                            placeholder="Search activity by user, action, details, timestamp..."
                            onReset={() => handleResetTab('activity', 'date-desc')}
                        />

                        {/* Top Pagination for seamless switching without scrolling */}
                        <TablePagination
                            currentPage={currentPage.activity}
                            totalItems={filteredActivity.length}
                            pageSize={100}
                            onPageChange={(page) => handlePageChange('activity', page)}
                            position="top"
                        />

                        <div className="domainer-table-container my-2">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="border-b border-white/10 bg-white/[0.02]">
                                            <th className="text-left py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Time</th>
                                            <th className="text-left py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">User</th>
                                            <th className="text-left py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Action</th>
                                            <th className="text-left py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Details</th>
                                        </tr>
                                    </thead>
                                <tbody>
                                    {filteredActivity.length === 0 ? (
                                        <AdminTableEmptyState
                                            colSpan={4}
                                            searchTerm={searchTerms.activity}
                                            itemLabel="activity logs"
                                            emptyMessage="No activity recorded yet"
                                            onReset={() => handleResetTab('activity', 'date-desc')}
                                        />
                                    ) : (
                                        paginatedActivity.map((log) => (
                                            <tr key={log.id} className="border-b border-dark-300/50 hover:bg-dark-200/30">
                                                <td className="py-3 px-4 text-neutral-400 whitespace-nowrap">
                                                    {formatTimestamp(log.timestamp)}
                                                </td>
                                                <td className="py-3 px-4">
                                                    <div className="text-white">
                                                        {log.first_name} {log.last_name}
                                                    </div>
                                                    {log.username && (
                                                        <div className="text-xs text-neutral-400">@{log.username}</div>
                                                    )}
                                                </td>
                                                <td className="py-3 px-4">
                                                    <span className="px-2 py-1 rounded text-xs font-semibold bg-dark-400 text-neutral-300">
                                                        {log.action}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4 text-neutral-300">
                                                    {log.details}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                            </div>
                        </div>
                        <TablePagination
                            currentPage={currentPage.activity}
                            totalItems={filteredActivity.length}
                            pageSize={100}
                            onPageChange={(page) => handlePageChange('activity', page)}
                        />
                    </div>
                )}

                {/* Settings Tab */}
                {activeTab === 'settings' && (
                    <div className="space-y-6 mb-8">
                        {/* Settings Header Bar */}
                        <div className="card p-6 bg-gradient-to-r from-dark-200/90 via-dark-200/60 to-dark-100 border border-dark-300">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                <div className="flex items-center gap-3.5">
                                    <div className="w-11 h-11 rounded-2xl bg-primary-500/15 border border-primary-500/30 flex items-center justify-center text-primary-400 shadow-inner">
                                        <SettingsIcon className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-display font-bold text-white tracking-wide">
                                            System & Infrastructure Settings
                                        </h2>
                                        <p className="text-xs text-neutral-400 mt-0.5">
                                            Manage Cloudflare DNS routing, Domain Watchdog monitoring, Anti-bot protection, and platform parameters
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2.5">
                                    <button
                                        onClick={handleTriggerCheckNow}
                                        disabled={isCheckingNow}
                                        className="px-4 py-2 rounded-xl text-xs font-semibold bg-primary-600 hover:bg-primary-500 disabled:opacity-50 text-white transition-all flex items-center gap-2 select-none shadow-md shadow-primary-500/20 cursor-pointer disabled:cursor-not-allowed active:scale-95"
                                        title="Trigger an immediate unscheduled verification for all domains"
                                    >
                                        <RefreshIcon className={`w-3.5 h-3.5 ${isCheckingNow ? 'animate-spin' : ''}`} />
                                        <span>{isCheckingNow ? 'Verifying all domains...' : 'Verify All Domains Now'}</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* 2-Column Grid of Settings Cards */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                            {/* Card 1: Cloudflare Integration & Target DNS */}
                            <div className="card p-6 border border-dark-300 flex flex-col justify-between">
                                <div>
                                    <div className="flex items-center justify-between pb-4 mb-4 border-b border-dark-300/60">
                                        <div className="flex items-center gap-2.5">
                                            <div className="w-8 h-8 rounded-lg bg-orange-500/15 border border-orange-500/30 flex items-center justify-center text-orange-400">
                                                <GlobeIcon className="w-4 h-4" />
                                            </div>
                                            <div>
                                                <h3 className="text-base font-bold text-white">Cloudflare DNS & Target IP</h3>
                                                <p className="text-[11px] text-neutral-400">Automated DNS A-Records management (@ and www)</p>
                                            </div>
                                        </div>
                                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 ${
                                            cloudflareConfigured
                                                ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                                                : 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                                        }`}>
                                            <span className={`w-1.5 h-1.5 rounded-full ${cloudflareConfigured ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400'}`} />
                                            {cloudflareConfigured ? 'Active / Configured' : 'No Token in .env'}
                                        </span>
                                    </div>

                                    <p className="text-xs text-neutral-300 leading-relaxed mb-4">
                                        All incoming parked domains, reverse-proxies, and rental forwarders route through Cloudflare to this central server IP. Changing this IP updates the target for all catalog domains.
                                    </p>

                                    {/* IP Setting Form */}
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-medium text-neutral-300 mb-1.5">
                                                Target Server IPv4 (<code className="text-orange-300">CLOUDFLARE_A_IP_SET</code>)
                                            </label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    value={cloudflareIp}
                                                    onChange={(e) => setCloudflareIp(e.target.value)}
                                                    placeholder="162.55.47.15"
                                                    className="flex-1 px-3 py-2 text-sm font-mono bg-dark-200 border border-dark-300 rounded-xl text-orange-300 focus:outline-none focus:border-orange-400 transition-colors"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleSaveCloudflareIp(cloudflareIp)}
                                                    disabled={isSavingCfIp}
                                                    className="px-4 py-2 rounded-xl text-xs font-semibold bg-orange-600 hover:bg-orange-500 disabled:opacity-50 text-white transition-all shadow-md shadow-orange-600/20 cursor-pointer disabled:cursor-not-allowed flex items-center gap-1.5"
                                                >
                                                    {isSavingCfIp ? (
                                                        <>
                                                            <RefreshIcon className="w-3.5 h-3.5 animate-spin" />
                                                            <span>Saving...</span>
                                                        </>
                                                    ) : (
                                                        <span>Save IP</span>
                                                    )}
                                                </button>
                                            </div>
                                            {cfIpSaveMessage && (
                                                <p className={`mt-2 text-xs font-medium ${cfIpSaveMessage.startsWith('Error') || cfIpSaveMessage.startsWith('Please') ? 'text-red-400' : 'text-emerald-400'}`}>
                                                    {cfIpSaveMessage}
                                                </p>
                                            )}
                                        </div>

                                        {/* Status Info Box */}
                                        <div className="p-3.5 rounded-xl bg-dark-200/50 border border-dark-300/60 space-y-2 text-xs">
                                            <div className="flex justify-between items-center text-neutral-400">
                                                <span>Last Synced IP:</span>
                                                <span className="font-mono text-neutral-200 font-semibold">
                                                    {cloudflareStatus?.lastSyncedIp || cloudflareIp || 'None'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-center text-neutral-400">
                                                <span>Last Synced Time:</span>
                                                <span className="text-neutral-200">
                                                    {cloudflareStatus?.lastSyncedAt ? formatTimestamp(cloudflareStatus.lastSyncedAt) : 'Never'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-center text-neutral-400">
                                                <span>Automated Sync:</span>
                                                <span className="text-emerald-400 font-medium">On domain approval & IP change</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-5 pt-4 border-t border-dark-300/60">
                                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                                        <button
                                            type="button"
                                            onClick={handleManualCloudflareSync}
                                            disabled={isSyncingCf || !cloudflareConfigured}
                                            className="w-full sm:w-auto px-4 py-2 rounded-xl text-xs font-semibold bg-dark-200 hover:bg-dark-300 disabled:opacity-40 text-neutral-200 border border-dark-300 transition-all flex items-center justify-center gap-2 cursor-pointer shadow-sm"
                                            title="Force synchronizing A-records across all domains in Cloudflare"
                                        >
                                            <RefreshIcon className={`w-3.5 h-3.5 ${isSyncingCf ? 'animate-spin text-orange-400' : 'text-neutral-400'}`} />
                                            <span>{isSyncingCf ? 'Syncing all records...' : 'Sync All Domains to Cloudflare'}</span>
                                        </button>
                                        {cfSyncMessage && (
                                            <span className={`text-xs font-medium ${cfSyncMessage.startsWith('Error') ? 'text-red-400' : 'text-emerald-400'}`}>
                                                {cfSyncMessage}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Card 2: Domain Watchdog & Monitoring */}
                            <div className="card p-6 border border-dark-300 flex flex-col justify-between">
                                <div>
                                    <div className="flex items-center justify-between pb-4 mb-4 border-b border-dark-300/60">
                                        <div className="flex items-center gap-2.5">
                                            <div className="w-8 h-8 rounded-lg bg-primary-500/15 border border-primary-500/30 flex items-center justify-center text-primary-400">
                                                <DogIcon className="w-4 h-4" />
                                            </div>
                                            <div>
                                                <h3 className="text-base font-bold text-white">Domain Watchdog Service</h3>
                                                <p className="text-[11px] text-neutral-400">Delegation verification & dead-link protection</p>
                                            </div>
                                        </div>
                                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 flex items-center gap-1.5">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                            Active ({watchdogInterval}m)
                                        </span>
                                    </div>

                                    <p className="text-xs text-neutral-300 leading-relaxed mb-4">
                                        Periodically probes all Parked and Offline domains via HMAC-SHA256 health checks. Automatically detects live traffic and unparks domains, or purges domains offline for over {watchdogHours} hours.
                                    </p>

                                    {/* Watchdog Parameters Grid */}
                                    <div className="grid grid-cols-2 gap-3 mb-4 text-xs">
                                        <div className="p-3 rounded-xl bg-dark-200/50 border border-dark-300/60">
                                            <span className="text-neutral-400 block mb-1">Check Frequency</span>
                                            <span className="font-bold text-white text-sm flex items-center gap-1.5">
                                                <ClockIcon className="w-4 h-4 text-primary-400" />
                                                Every {watchdogInterval} min
                                            </span>
                                            <span className="text-[10px] text-neutral-500 font-mono mt-1 block">DOMAIN_CHECK_INTERVAL_MINUTES</span>
                                        </div>
                                        <div className="p-3 rounded-xl bg-dark-200/50 border border-dark-300/60">
                                            <span className="text-neutral-400 block mb-1">Auto-Purge Threshold</span>
                                            <span className="font-bold text-red-400 text-sm flex items-center gap-1.5">
                                                <TrashIcon className="w-4 h-4 text-red-400" />
                                                {watchdogHours} hours offline
                                            </span>
                                            <span className="text-[10px] text-neutral-500 font-mono mt-1 block">DOMAIN_UNDELEGATE_HOURS</span>
                                        </div>
                                    </div>

                                    {/* 4 Pillars Collapsible Guide */}
                                    <div className="rounded-xl border border-dark-300/60 bg-dark-200/30 overflow-hidden">
                                        <button
                                            type="button"
                                            onClick={toggleWatchdogCollapsed}
                                            className="w-full px-3.5 py-2.5 text-xs font-semibold text-neutral-300 hover:text-white flex items-center justify-between transition-colors cursor-pointer"
                                        >
                                            <span className="flex items-center gap-2">
                                                <ShieldIcon className="w-3.5 h-3.5 text-primary-400" />
                                                Watchdog 4-Pillar Protection Architecture
                                            </span>
                                            <span className="text-[11px] text-neutral-400 underline decoration-dotted">
                                                {isWatchdogCollapsed ? 'Expand guide ▼' : 'Collapse guide ▲'}
                                            </span>
                                        </button>
                                        {!isWatchdogCollapsed && (
                                            <div className="p-3.5 pt-0 space-y-2 text-xs border-t border-dark-300/40 mt-1">
                                                <div className="flex items-start gap-2 pt-2">
                                                    <ClockIcon className="w-3.5 h-3.5 text-primary-400 shrink-0 mt-0.5" />
                                                    <p className="text-neutral-400 text-[11px] leading-relaxed">
                                                        <strong className="text-neutral-200">Periodic Verification:</strong> Queries <code className="text-primary-300">/health</code> across all Parked and Offline domains every {watchdogInterval} minutes with HMAC-SHA256 signature.
                                                    </p>
                                                </div>
                                                <div className="flex items-start gap-2">
                                                    <TrashIcon className="w-3.5 h-3.5 text-red-400 shrink-0 mt-0.5" />
                                                    <p className="text-neutral-400 text-[11px] leading-relaxed">
                                                        <strong className="text-neutral-200">Dead-link Purge:</strong> Continuous failure for $\ge {watchdogHours}$ hours unparks the domain and notifies the owner.
                                                    </p>
                                                </div>
                                                <div className="flex items-start gap-2">
                                                    <ZapIcon className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                                                    <p className="text-neutral-400 text-[11px] leading-relaxed">
                                                        <strong className="text-neutral-200">Instant Traffic Check:</strong> Any incoming visitor request to an Offline domain runs an instant HMAC probe to restore status immediately.
                                                    </p>
                                                </div>
                                                <div className="flex items-start gap-2">
                                                    <ShieldIcon className="w-3.5 h-3.5 text-sky-400 shrink-0 mt-0.5" />
                                                    <p className="text-neutral-400 text-[11px] leading-relaxed">
                                                        <strong className="text-neutral-200">Inactive Exclusion:</strong> Domains set to Inactive are never auto-checked and never auto-purged.
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="mt-5 pt-4 border-t border-dark-300/60 flex justify-end">
                                    <button
                                        type="button"
                                        onClick={handleTriggerCheckNow}
                                        disabled={isCheckingNow}
                                        className="w-full sm:w-auto px-4 py-2 rounded-xl text-xs font-semibold bg-primary-600 hover:bg-primary-500 disabled:opacity-50 text-white transition-all flex items-center justify-center gap-2 cursor-pointer shadow-md shadow-primary-500/20"
                                    >
                                        <RefreshIcon className={`w-3.5 h-3.5 ${isCheckingNow ? 'animate-spin' : ''}`} />
                                        <span>{isCheckingNow ? 'Verifying all domains...' : 'Trigger Immediate Check'}</span>
                                    </button>
                                </div>
                            </div>

                            {/* Card 3: Anti-Bot Protection (Cloudflare Turnstile) */}
                            <div className="card p-6 border border-dark-300 flex flex-col justify-between">
                                <div>
                                    <div className="flex items-center justify-between pb-4 mb-4 border-b border-dark-300/60">
                                        <div className="flex items-center gap-2.5">
                                            <div className="w-8 h-8 rounded-lg bg-teal-500/15 border border-teal-500/30 flex items-center justify-center text-teal-400">
                                                <ShieldIcon className="w-4 h-4" />
                                            </div>
                                            <div>
                                                <h3 className="text-base font-bold text-white">Anti-Bot Protection (Turnstile)</h3>
                                                <p className="text-[11px] text-neutral-400">Cloudflare Turnstile challenge & rate limiting</p>
                                            </div>
                                        </div>
                                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 ${
                                            systemSettings?.turnstileConfigured
                                                ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                                                : 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                                        }`}>
                                            <span className={`w-1.5 h-1.5 rounded-full ${systemSettings?.turnstileConfigured ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                                            {systemSettings?.turnstileConfigured ? 'Active & Enforced' : 'Bypass / Optional'}
                                        </span>
                                    </div>

                                    <p className="text-xs text-neutral-300 leading-relaxed mb-4">
                                        Protects domain search, WHOIS lookups, and forms against automated scrapers without annoying captchas. Verified server-to-server via Cloudflare Challenges API.
                                    </p>

                                    {/* Parameters Display */}
                                    <div className="space-y-3 text-xs mb-4">
                                        <div className="p-3 rounded-xl bg-dark-200/50 border border-dark-300/60 space-y-1.5">
                                            <span className="text-neutral-400 block text-[11px]">Public Site Key (<code className="text-teal-300">REACT_APP_CLOUDFLARE_TURNSTILE_SITE_KEY</code>)</span>
                                            <code className="text-xs font-mono text-neutral-200 break-all select-all block">
                                                {systemSettings?.turnstileSiteKey || 'Not configured in .env'}
                                            </code>
                                        </div>
                                        <div className="p-3 rounded-xl bg-dark-200/50 border border-dark-300/60 space-y-1.5">
                                            <span className="text-neutral-400 block text-[11px]">Server Secret Key (<code className="text-teal-300">CLOUDFLARE_TURNSTILE_SECRET_KEY</code>)</span>
                                            <span className="text-xs font-mono text-neutral-400 block">
                                                {systemSettings?.turnstileConfigured ? '•••••••••••••••••••••••••••••••• (Configured in .env)' : 'Not configured in .env (bypassed)'}
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div className="p-2.5 rounded-lg bg-dark-200/40 border border-dark-300/40">
                                                <span className="text-neutral-400 block text-[10px]">Rate Limit Cooldown</span>
                                                <span className="font-bold text-white text-xs">{systemSettings?.rateLimitSeconds || 5}s per IP</span>
                                            </div>
                                            <div className="p-2.5 rounded-lg bg-dark-200/40 border border-dark-300/40">
                                                <span className="text-neutral-400 block text-[10px]">Response Cache</span>
                                                <span className="font-bold text-white text-xs">{systemSettings?.cacheHours || 12} hours</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-4 pt-3 border-t border-dark-300/60 text-[11px] text-neutral-400 flex items-center justify-between">
                                    <span>Detailed setup guide available in <code className="text-primary-300 font-mono">README.md</code></span>
                                    <span className="text-teal-400 font-medium">Auto-bypass if empty</span>
                                </div>
                            </div>

                            {/* Card 4: Platform & Environment Configuration */}
                            <div className="card p-6 border border-dark-300 flex flex-col justify-between">
                                <div>
                                    <div className="flex items-center justify-between pb-4 mb-4 border-b border-dark-300/60">
                                        <div className="flex items-center gap-2.5">
                                            <div className="w-8 h-8 rounded-lg bg-primary-500/15 border border-primary-500/30 flex items-center justify-center text-primary-400">
                                                <ZapIcon className="w-4 h-4" />
                                            </div>
                                            <div>
                                                <h3 className="text-base font-bold text-white">Marketplace & Node.js Platform</h3>
                                                <p className="text-[11px] text-neutral-400">Environment variables & runtime options</p>
                                            </div>
                                        </div>
                                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-dark-200 text-neutral-300 border border-dark-300">
                                            {systemSettings?.nodeEnv || 'production'}
                                        </span>
                                    </div>

                                    <p className="text-xs text-neutral-300 leading-relaxed mb-4">
                                        Core platform options configured via <code className="text-primary-300">.env</code>. To update values, modify the server <code className="text-primary-300">.env</code> and restart with <code className="text-primary-300">pm2 restart domainer-app --update-env</code>.
                                    </p>

                                    {/* Parameter Key-Value Grid */}
                                    <div className="space-y-2 text-xs">
                                        <div className="flex justify-between items-center p-2.5 rounded-lg bg-dark-200/50 border border-dark-300/60">
                                            <span className="text-neutral-400">Primary Marketplace Host:</span>
                                            <span className="font-mono text-primary-300 font-bold">{systemSettings?.primaryHost || 'buy.tg'}</span>
                                        </div>
                                        <div className="flex justify-between items-center p-2.5 rounded-lg bg-dark-200/50 border border-dark-300/60">
                                            <span className="text-neutral-400">WHOIS Lookup Mode:</span>
                                            <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${
                                                systemSettings?.whoisMode === 'full' ? 'bg-primary-500/20 text-primary-300' :
                                                systemSettings?.whoisMode === 'min' ? 'bg-amber-500/20 text-amber-300' : 'bg-red-500/20 text-red-300'
                                            }`}>
                                                {systemSettings?.whoisMode?.toUpperCase() || 'OFF'} (via .env)
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center p-2.5 rounded-lg bg-dark-200/50 border border-dark-300/60">
                                            <span className="text-neutral-400">Partner Registrator URL:</span>
                                            <span className="font-mono text-neutral-200">{systemSettings?.partnerRegistratorUrl || 'https://nic.tg'}</span>
                                        </div>
                                        <div className="flex justify-between items-center p-2.5 rounded-lg bg-dark-200/50 border border-dark-300/60">
                                            <span className="text-neutral-400">Minimum Listing Prices:</span>
                                            <span className="text-emerald-400 font-medium">
                                                Buy ${minPrices.min_price_buy || 100} / Rent ${minPrices.min_price_rent || 50}/mo / FW ${minPrices.min_price_forward || 10}/day
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center p-2.5 rounded-lg bg-dark-200/50 border border-dark-300/60">
                                            <span className="text-neutral-400">Telegram Bot Username:</span>
                                            <span className="text-neutral-200 font-medium">
                                                {systemSettings?.botUsername ? `@${systemSettings.botUsername}` : 'Not configured'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-4 pt-3 border-t border-dark-300/60 text-[11px] text-neutral-500 flex items-center justify-between">
                                    <span>Single Source of Truth: <code className="text-neutral-400">.env</code></span>
                                    <span>Domainer Engine v0.99</span>
                                </div>
                            </div>

                        </div>
                    </div>
                )}

                {/* Edit Domain Modal */}
                {showEditModal && (
                    <div 
                        className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 cursor-pointer"
                        onClick={(e) => {
                            if (e.target === e.currentTarget) {
                                setShowEditModal(false);
                            }
                        }}
                    >
                        <div className="card p-6 max-w-md w-full max-h-[90vh] overflow-y-auto cursor-default" onClick={(e) => e.stopPropagation()}>
                            <h3 className="text-lg font-display font-bold text-white mb-6">Edit Domain</h3>
                            {editFormErrors.general && (
                                <div className="mb-4 p-3 bg-red-500/20 text-red-300 rounded-lg text-sm">
                                    {editFormErrors.general}
                                </div>
                            )}
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-neutral-300 mb-1">Domain Name</label>
                                    <input 
                                        type="text" 
                                        value={editForm.name}
                                        onChange={(e) => {
                                            setEditForm({...editForm, name: e.target.value});
                                            if (editFormErrors.name) {
                                                setEditFormErrors({...editFormErrors, name: ''});
                                            }
                                        }}
                                        className={`w-full px-3 py-2 bg-dark-200 border rounded-lg text-white focus:outline-none focus:ring-2 ${
                                            editFormErrors.name 
                                                ? 'border-red-500 focus:ring-red-500/30' 
                                                : 'border-dark-300 focus:ring-primary-500/30'
                                        }`}
                                        placeholder="example.com"
                                    />
                                    {editFormErrors.name && (
                                        <p className="mt-1 text-xs text-red-400">{editFormErrors.name}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-neutral-300 mb-1">Description</label>
                                    <input 
                                        type="text" 
                                        value={editForm.description}
                                        onChange={(e) => {
                                            setEditForm({...editForm, description: e.target.value});
                                            if (editFormErrors.description) {
                                                setEditFormErrors({...editFormErrors, description: ''});
                                            }
                                        }}
                                        className={`w-full px-3 py-2 bg-dark-200 border rounded-lg text-white focus:outline-none focus:ring-2 ${
                                            editFormErrors.description 
                                                ? 'border-red-500 focus:ring-red-500/30' 
                                                : 'border-dark-300 focus:ring-primary-500/30'
                                        }`}
                                        placeholder="Domain description (use #hashtags e.g. #crypto #ai)"
                                    />
                                    <p className="mt-1 text-[11px] text-neutral-400">
                                        Tip: Use <span className="text-primary-400 font-mono">#hashtags</span> for search categories and tag filters (e.g. #crypto #fintech #ton).
                                    </p>
                                    {editFormErrors.description && (
                                        <p className="mt-1 text-xs text-red-400">{editFormErrors.description}</p>
                                    )}
                                </div>
                                <div className="pt-2">
                                    <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">Listing Options</p>
                                    
                                    {/* Option 1: Buy */}
                                    <div className={`p-3.5 rounded-xl border mb-3 transition-colors ${editForm.enable_buy ? 'bg-primary-500/5 border-primary-500/30' : 'bg-dark-200/30 border-dark-300/40'}`}>
                                        <label className="flex items-center gap-3 cursor-pointer select-none">
                                            <input 
                                                type="checkbox"
                                                checked={editForm.enable_buy}
                                                onChange={(e) => setEditForm({...editForm, enable_buy: e.target.checked})}
                                                className="w-4 h-4 rounded text-primary-500 bg-dark-300 border-dark-300 focus:ring-primary-500"
                                            />
                                            <span className="text-white font-medium text-sm flex items-center gap-1.5"><TagIcon className="w-3.5 h-3.5 text-primary-400" /> Sale</span>
                                            <span className="text-xs text-neutral-400 ml-auto">Min: ${minPrices.min_price_buy || 100}</span>
                                        </label>
                                        {editForm.enable_buy && (
                                            <div className="grid grid-cols-2 gap-3 pt-3 mt-2 border-t border-dark-300/40">
                                                <div>
                                                    <label className="text-xs text-neutral-400 block mb-1">Buy price ($)</label>
                                                    <input 
                                                        type="number" 
                                                        value={editForm.pricebuy}
                                                        onChange={(e) => {
                                                            setEditForm({...editForm, pricebuy: e.target.value});
                                                            if (editFormErrors.pricebuy) {
                                                                setEditFormErrors({...editFormErrors, pricebuy: ''});
                                                            }
                                                        }}
                                                        className={`w-full px-3 py-2 bg-dark-200 border rounded-lg text-white focus:outline-none focus:ring-2 ${
                                                            editFormErrors.pricebuy 
                                                                ? 'border-red-500 focus:ring-red-500/30' 
                                                                : 'border-dark-300 focus:ring-primary-500/30'
                                                        }`}
                                                        placeholder={String(minPrices.min_price_buy || 100)}
                                                    />
                                                    {editFormErrors.pricebuy && (
                                                        <p className="mt-1 text-xs text-red-400">{editFormErrors.pricebuy}</p>
                                                    )}
                                                </div>
                                                <div>
                                                    <label className="text-xs text-neutral-400 block mb-1">Buy Discount (%)</label>
                                                    <input 
                                                        type="number" 
                                                        value={editForm.discount_buy}
                                                        onChange={(e) => {
                                                            setEditForm({...editForm, discount_buy: e.target.value});
                                                        }}
                                                        className="w-full px-3 py-2 bg-dark-200 border rounded-lg text-white focus:outline-none focus:ring-2 border-dark-300 focus:ring-primary-500/30"
                                                        placeholder="0"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Option 2: Rent */}
                                    <div className={`p-3.5 rounded-xl border mb-3 transition-colors ${editForm.enable_rent ? 'bg-secondary-500/5 border-secondary-500/30' : 'bg-dark-200/30 border-dark-300/40'}`}>
                                        <div className="flex items-center justify-between">
                                            <label className="flex items-center gap-3 cursor-pointer select-none">
                                                <input 
                                                    type="checkbox"
                                                    checked={editForm.enable_rent}
                                                    onChange={(e) => setEditForm({...editForm, enable_rent: e.target.checked})}
                                                    className="w-4 h-4 rounded text-secondary-500 bg-dark-300 border-dark-300 focus:ring-secondary-500"
                                                />
                                                <span className="text-white font-medium text-sm flex items-center gap-1.5"><ClockIcon className="w-3.5 h-3.5 text-secondary-400" /> Rent</span>
                                                <span className="text-xs text-neutral-400 ml-2">Min: ${minPrices.min_price_rent || 50}/mo</span>
                                            </label>
                                            {editForm.enable_rent && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const calculated = editForm.pricebuy ? Math.ceil(Number(editForm.pricebuy) * 0.05) : '';
                                                        setEditForm({...editForm, pricerent: calculated});
                                                        if (editFormErrors.pricerent) setEditFormErrors({...editFormErrors, pricerent: ''});
                                                    }}
                                                    className="text-xs text-primary-400 hover:text-primary-300"
                                                >
                                                    Auto-fill 5%
                                                </button>
                                            )}
                                        </div>
                                        {editForm.enable_rent && (
                                            <div className="grid grid-cols-2 gap-3 pt-3 mt-2 border-t border-dark-300/40">
                                                <div>
                                                    <label className="text-xs text-neutral-400 block mb-1">Rent Price ($/month)</label>
                                                    <input 
                                                        type="number" 
                                                        value={editForm.pricerent}
                                                        onChange={(e) => {
                                                            setEditForm({...editForm, pricerent: e.target.value});
                                                            if (editFormErrors.pricerent) {
                                                                setEditFormErrors({...editFormErrors, pricerent: ''});
                                                            }
                                                        }}
                                                        className={`w-full px-3 py-2 bg-dark-200 border rounded-lg text-white focus:outline-none focus:ring-2 ${
                                                            editFormErrors.pricerent 
                                                                ? 'border-red-500 focus:ring-red-500/30' 
                                                                : 'border-dark-300 focus:ring-secondary-500/30'
                                                        }`}
                                                        placeholder={String(minPrices.min_price_rent || 50)}
                                                    />
                                                    {editFormErrors.pricerent && (
                                                        <p className="mt-1 text-xs text-red-400">{editFormErrors.pricerent}</p>
                                                    )}
                                                </div>
                                                <div>
                                                    <label className="text-xs text-neutral-400 block mb-1">Rent Discount (%)</label>
                                                    <input 
                                                        type="number" 
                                                        value={editForm.discount_rent}
                                                        onChange={(e) => {
                                                            setEditForm({...editForm, discount_rent: e.target.value});
                                                        }}
                                                        className="w-full px-3 py-2 bg-dark-200 border rounded-lg text-white focus:outline-none focus:ring-2 border-dark-300 focus:ring-secondary-500/30"
                                                        placeholder="0"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Option 3: Forwarding */}
                                    <div className={`p-3.5 rounded-xl border mb-3 transition-colors ${editForm.enable_forward ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-dark-200/30 border-dark-300/40'}`}>
                                        <div className="flex items-center justify-between">
                                            <label className="flex items-center gap-3 cursor-pointer select-none">
                                                <input 
                                                    type="checkbox"
                                                    checked={editForm.enable_forward}
                                                    onChange={(e) => setEditForm({...editForm, enable_forward: e.target.checked})}
                                                    className="w-4 h-4 rounded text-emerald-500 bg-dark-300 border-dark-300 focus:ring-emerald-500"
                                                />
                                                <span className="text-white font-medium text-sm">🔀 Forwarding</span>
                                                <span className="text-xs text-neutral-400 ml-2">Min: ${minPrices.min_price_forward || 10}/day</span>
                                            </label>
                                            {editForm.enable_forward && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const calculated = editForm.pricerent ? Math.ceil(Number(editForm.pricerent) / 30) : '';
                                                        setEditForm({...editForm, pricefw: calculated});
                                                        if (editFormErrors.pricefw) setEditFormErrors({...editFormErrors, pricefw: ''});
                                                    }}
                                                    className="text-xs text-primary-400 hover:text-primary-300"
                                                >
                                                    Auto-fill Rent/30
                                                </button>
                                            )}
                                        </div>
                                        {editForm.enable_forward && (
                                            <div className="grid grid-cols-2 gap-3 pt-3 mt-2 border-t border-dark-300/40">
                                                <div>
                                                    <label className="text-xs text-neutral-400 block mb-1">Forward Price ($/day)</label>
                                                    <input 
                                                        type="number" 
                                                        value={editForm.pricefw}
                                                        onChange={(e) => {
                                                            setEditForm({...editForm, pricefw: e.target.value});
                                                            if (editFormErrors.pricefw) {
                                                                setEditFormErrors({...editFormErrors, pricefw: ''});
                                                            }
                                                        }}
                                                        className={`w-full px-3 py-2 bg-dark-200 border rounded-lg text-white focus:outline-none focus:ring-2 ${
                                                            editFormErrors.pricefw 
                                                                ? 'border-red-500 focus:ring-red-500/30' 
                                                                : 'border-dark-300 focus:ring-emerald-500/30'
                                                        }`}
                                                        placeholder={String(minPrices.min_price_forward || 10)}
                                                    />
                                                    {editFormErrors.pricefw && (
                                                        <p className="mt-1 text-xs text-red-400">{editFormErrors.pricefw}</p>
                                                    )}
                                                </div>
                                                <div>
                                                    <label className="text-xs text-neutral-400 block mb-1">Forward Discount (%)</label>
                                                    <input 
                                                        type="number" 
                                                        value={editForm.discount_fw}
                                                        onChange={(e) => {
                                                            setEditForm({...editForm, discount_fw: e.target.value});
                                                        }}
                                                        className="w-full px-3 py-2 bg-dark-200 border rounded-lg text-white focus:outline-none focus:ring-2 border-dark-300 focus:ring-emerald-500/30"
                                                        placeholder="0"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="block text-sm font-medium text-neutral-300">
                                            Owner ID <span className="text-red-400">*</span>
                                        </label>
                                        {selectedOwnerUser && (
                                            <span className="text-xs text-primary-400 font-mono">
                                                #{selectedOwnerUser.id} {selectedOwnerUser.username ? `@${selectedOwnerUser.username}` : selectedOwnerUser.first_name}
                                            </span>
                                        )}
                                    </div>

                                    {/* Search & Select Controls */}
                                    <div className="space-y-2">
                                        <div className="relative">
                                            <input 
                                                type="text"
                                                value={ownerSearchQuery}
                                                onChange={(e) => setOwnerSearchQuery(e.target.value)}
                                                placeholder="Filter users by name, @username, or TG ID..."
                                                className="w-full pl-8 pr-7 py-1.5 bg-dark-300/80 border border-dark-300 rounded-lg text-xs text-white placeholder-neutral-500 focus:outline-none focus:ring-1 focus:ring-primary-500/50"
                                            />
                                            <span className="absolute left-2.5 top-2 text-neutral-500 pointer-events-none"><SearchIcon className="w-3.5 h-3.5" /></span>
                                            {ownerSearchQuery && (
                                                <button
                                                    type="button"
                                                    onClick={() => setOwnerSearchQuery('')}
                                                    className="absolute right-2.5 top-1.5 text-neutral-400 hover:text-white"
                                                    title="Clear filter"
                                                >
                                                    <CrossIcon className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                                            <div className="sm:col-span-1">
                                                <input 
                                                    type="number" 
                                                    value={editForm.owner_id}
                                                    onChange={(e) => {
                                                        setEditForm({...editForm, owner_id: e.target.value});
                                                        if (editFormErrors.owner_id) {
                                                            setEditFormErrors(prev => ({...prev, owner_id: ''}));
                                                        }
                                                    }}
                                                    className={`w-full px-3 py-2 bg-dark-200 border rounded-lg text-white font-mono text-sm focus:outline-none focus:ring-2 ${
                                                        editFormErrors.owner_id ? 'border-red-500 ring-1 ring-red-500' : 'border-dark-300 focus:ring-primary-500/30'
                                                    }`}
                                                    placeholder="ID #"
                                                    min="1"
                                                    required
                                                />
                                            </div>
                                            <div className="sm:col-span-3">
                                                <select
                                                    value={editForm.owner_id || ''}
                                                    onChange={(e) => {
                                                        setEditForm({...editForm, owner_id: e.target.value});
                                                        if (editFormErrors.owner_id) {
                                                            setEditFormErrors(prev => ({...prev, owner_id: ''}));
                                                        }
                                                    }}
                                                    className={`w-full px-3 py-2 bg-dark-200 border rounded-lg text-white text-sm focus:outline-none focus:ring-2 ${
                                                        editFormErrors.owner_id ? 'border-red-500 ring-1 ring-red-500' : 'border-dark-300 focus:ring-primary-500/30'
                                                    }`}
                                                >
                                                    <option value="">-- Select user from list --</option>
                                                    {filteredOwnerUsers.map((u) => (
                                                        <option key={u.id} value={u.id}>
                                                            #{u.id} — {u.first_name || ''} {u.last_name || ''} {u.username ? `(@${u.username})` : ''} [TG: {u.telegram_id}]
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    {editFormErrors.owner_id && (
                                        <p className="mt-1 text-xs text-red-400">{editFormErrors.owner_id}</p>
                                    )}

                                    {/* Selected User Details Card */}
                                    {selectedOwnerUser ? (
                                        <div className="flex items-center justify-between p-2.5 mt-2 bg-dark-300/60 border border-primary-500/30 rounded-lg text-xs">
                                            <div className="flex items-center gap-2.5">
                                                <div className="w-7 h-7 rounded-full bg-primary-600/30 text-primary-300 flex items-center justify-center font-bold text-[11px] border border-primary-500/30 select-none flex-shrink-0">
                                                    {selectedOwnerUser.first_name ? selectedOwnerUser.first_name[0].toUpperCase() : (selectedOwnerUser.username ? selectedOwnerUser.username[0].toUpperCase() : `#${selectedOwnerUser.id}`)}
                                                </div>
                                                <div>
                                                    <div className="text-white font-medium flex items-center gap-1.5">
                                                        <span>{selectedOwnerUser.first_name} {selectedOwnerUser.last_name || ''}</span>
                                                        {selectedOwnerUser.username && (
                                                            <span className="text-neutral-400 text-[11px]">(@{selectedOwnerUser.username})</span>
                                                        )}
                                                    </div>
                                                    <div className="text-[10px] text-neutral-400 font-mono">
                                                        TG ID: {selectedOwnerUser.telegram_id}
                                                    </div>
                                                </div>
                                            </div>
                                            <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-primary-500/20 text-primary-300 border border-primary-500/30 font-semibold">
                                                Owner #{selectedOwnerUser.id}
                                            </span>
                                        </div>
                                    ) : editForm.owner_id ? (
                                        <div className="flex items-center justify-between p-2 mt-2 bg-dark-300/40 border border-dark-400 rounded-lg text-xs text-neutral-400">
                                            <span>Owner ID specified: <strong className="text-white font-mono">#{editForm.owner_id}</strong></span>
                                            <span className="text-[10px] text-neutral-500">(Not in active users list)</span>
                                        </div>
                                    ) : null}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-neutral-300 mb-1">Domain Status</label>
                                    <select
                                        value={editForm.status}
                                        onChange={(e) => setEditForm({...editForm, status: Number(e.target.value)})}
                                        className="w-full px-3 py-2 bg-dark-200 border rounded-lg text-white focus:outline-none focus:ring-2 border-dark-300 focus:ring-primary-500/30"
                                    >
                                        <option value={1}>Parked (Active)</option>
                                        <option value={0}>Offline</option>
                                        <option value={-1}>Inactive</option>
                                    </select>
                                </div>
                            </div>
                            <div className="flex justify-end space-x-3 mt-6">
                                <button 
                                    onClick={() => setShowEditModal(false)}
                                    className="btn btn-ghost px-4 py-2 text-sm"
                                >
                                    Cancel
                                </button>
                                <button 
                                    onClick={handleUpdateDomain}
                                    className="btn btn-primary px-4 py-2 text-sm"
                                >
                                    <span className="relative z-10">Update Domain</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Add Domain Modal using unified ContactForm */}
                {showAddModal && (
                    <div 
                        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black bg-opacity-70 backdrop-blur-sm"
                        onClick={(e) => {
                            if (e.target === e.currentTarget) {
                                setShowAddModal(false);
                            }
                        }}
                    >
                        <div className="relative">
                            <ContactForm 
                                onClose={() => setShowAddModal(false)} 
                                isPurchase={false}
                                currentUser={currentUser}
                                authConfigProp={{ authEnabled: true, botUsername: '' }}
                                onSubmitted={() => {
                                    setShowAddModal(false);
                                    fetchAdminData(adminToken);
                                }}
                            />
                        </div>
                    </div>
                )}

                {/* Edit Action Modal */}
                {showEditActionModal && (
                    <div 
                        className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 cursor-pointer"
                        onClick={(e) => {
                            if (e.target === e.currentTarget) {
                                setShowEditActionModal(false);
                            }
                        }}
                    >
                        <div className="card p-6 max-w-md w-full cursor-default" onClick={(e) => e.stopPropagation()}>
                            <h3 className="text-xl font-semibold text-white mb-4">Edit Action</h3>
                            <form onSubmit={handleEditActionSubmit}>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-neutral-300 mb-1">Destination</label>
                                        <input 
                                            type="text" 
                                            value={editActionForm.destination}
                                            onChange={(e) => setEditActionForm({...editActionForm, destination: e.target.value})}
                                            className="w-full px-3 py-2 bg-dark-200 border rounded-lg text-white focus:outline-none focus:ring-2 border-dark-300 focus:ring-primary-500/30"
                                            placeholder="URL or IP"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-neutral-300 mb-1">End Time</label>
                                        <input 
                                            type="datetime-local" 
                                            value={editActionForm.end_time}
                                            onChange={(e) => setEditActionForm({...editActionForm, end_time: e.target.value})}
                                            className="w-full px-3 py-2 bg-dark-200 border rounded-lg text-white focus:outline-none focus:ring-2 border-dark-300 focus:ring-primary-500/30"
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end space-x-3 mt-6">
                                    <button 
                                        type="button"
                                        onClick={() => setShowEditActionModal(false)}
                                        className="btn btn-ghost px-4 py-2 text-sm"
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit"
                                        className="btn btn-primary px-4 py-2 text-sm"
                                    >
                                        <span className="relative z-10">Save</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Invoice & Payment Details Modal */}
                {showOrderDetailsModal && selectedOrderDetails && (() => {
                    let parsedDetails = null;
                    if (selectedOrderDetails.payment_details) {
                        try {
                            parsedDetails = typeof selectedOrderDetails.payment_details === 'string'
                                ? JSON.parse(selectedOrderDetails.payment_details)
                                : selectedOrderDetails.payment_details;
                        } catch (e) {}
                    }

                    const paymentMethodDisplay = selectedOrderDetails.payment_method
                        || (parsedDetails && parsedDetails.payment_method)
                        || (selectedOrderDetails.cryptobot_invoice_id ? 'CryptoBot (USDT)' : null)
                        || (selectedOrderDetails.invoice_type === 'stars' ? 'Telegram Stars ⭐' : null)
                        || 'CryptoBot / Online Payment';

                    const orderRefDisplay = selectedOrderDetails.order_ref
                        || (parsedDetails && parsedDetails.order_ref)
                        || `ORD-${selectedOrderDetails.id}`;

                    const invoiceIdDisplay = selectedOrderDetails.cryptobot_invoice_id
                        || (parsedDetails && parsedDetails.invoice_id)
                        || 'N/A';

                    return (
                        <div 
                            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 cursor-pointer"
                            onClick={(e) => {
                                if (e.target === e.currentTarget) {
                                    setShowOrderDetailsModal(false);
                                }
                            }}
                        >
                            <div className="card p-6 max-w-xl w-full border border-dark-300 bg-dark-100 shadow-2xl rounded-2xl max-h-[90vh] overflow-y-auto cursor-default" onClick={(e) => e.stopPropagation()}>
                                <div className="flex justify-between items-start border-b border-dark-300 pb-4 mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 text-xl">
                                            🧾
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold text-white">Payment & Invoice Details</h3>
                                            <p className="text-xs text-neutral-400 font-mono mt-0.5">Order Ref: {orderRefDisplay}</p>
                                        </div>
                                    </div>
                                    <button 
                                        onClick={() => setShowOrderDetailsModal(false)}
                                        className="text-neutral-400 hover:text-white p-1 rounded-lg hover:bg-dark-200 transition-colors"
                                        title="Close"
                                    >
                                        <CrossIcon className="w-5 h-5" />
                                    </button>
                                </div>

                                {/* Amount Banner */}
                                <div className="bg-emerald-950/40 border border-emerald-500/30 rounded-xl p-4 mb-5 flex justify-between items-center">
                                    <div>
                                        <span className="text-xs text-emerald-300 uppercase tracking-wider font-semibold">Total Paid</span>
                                        <div className="text-3xl font-extrabold text-white mt-0.5">
                                            ${selectedOrderDetails.price_paid} <span className="text-sm font-medium text-emerald-400">USD</span>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                                            ✓ Paid / Completed
                                        </span>
                                        <p className="text-[11px] text-neutral-400 mt-1 font-mono">
                                            {formatTimestamp(selectedOrderDetails.start_time)}
                                        </p>
                                    </div>
                                </div>

                                {/* Details Grid */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-5">
                                    <div className="bg-dark-200/50 p-3 rounded-lg border border-dark-300/60">
                                        <span className="text-xs text-neutral-400 block mb-1">Domain</span>
                                        <span className="text-primary-400 font-semibold text-base">{selectedOrderDetails.domain_name}</span>
                                    </div>

                                    <div className="bg-dark-200/50 p-3 rounded-lg border border-dark-300/60">
                                        <span className="text-xs text-neutral-400 block mb-1">Action Type</span>
                                        <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${
                                            selectedOrderDetails.action_type === 'buy' ? 'bg-blue-500/20 text-blue-400' :
                                            selectedOrderDetails.action_type === 'rent' ? 'bg-purple-500/20 text-purple-400' :
                                            'bg-emerald-500/20 text-emerald-400'
                                        }`}>
                                            {selectedOrderDetails.action_type}
                                        </span>
                                        {selectedOrderDetails.duration && (
                                            <span className="ml-2 text-xs text-neutral-300">
                                                ({selectedOrderDetails.duration} {selectedOrderDetails.action_type === 'rent' ? 'months' : 'days'})
                                            </span>
                                        )}
                                    </div>

                                    <div className="bg-dark-200/50 p-3 rounded-lg border border-dark-300/60">
                                        <span className="text-xs text-neutral-400 block mb-1">Payment Method</span>
                                        <span className="text-neutral-100 font-medium flex items-center gap-1.5">
                                            <span>💳</span> {paymentMethodDisplay}
                                        </span>
                                    </div>

                                    <div className="bg-dark-200/50 p-3 rounded-lg border border-dark-300/60">
                                        <span className="text-xs text-neutral-400 block mb-1">Invoice ID</span>
                                        <span className="text-neutral-200 font-mono text-xs">{invoiceIdDisplay}</span>
                                    </div>

                                    <div className="bg-dark-200/50 p-3 rounded-lg border border-dark-300/60">
                                        <span className="text-xs text-neutral-400 block mb-1">Customer</span>
                                        <div className="text-neutral-100 font-medium">
                                            {selectedOrderDetails.first_name || ''} {selectedOrderDetails.last_name || ''}
                                            {selectedOrderDetails.username && (
                                                <a 
                                                    href={`https://t.me/${selectedOrderDetails.username}`}
                                                    target="_blank" 
                                                    rel="noreferrer"
                                                    className="block text-xs text-primary-400 hover:underline mt-0.5"
                                                >
                                                    @{selectedOrderDetails.username}
                                                </a>
                                            )}
                                        </div>
                                    </div>

                                    <div className="bg-dark-200/50 p-3 rounded-lg border border-dark-300/60">
                                        <span className="text-xs text-neutral-400 block mb-1">Target / Destination</span>
                                        <span className="text-neutral-200 font-mono text-xs break-all">
                                            {selectedOrderDetails.destination || 'Direct acquisition (transfer)'}
                                        </span>
                                    </div>
                                </div>

                                {/* Period Info */}
                                <div className="bg-dark-200/30 p-3 rounded-lg border border-dark-300/40 text-xs mb-5 space-y-1.5">
                                    <div className="flex justify-between">
                                        <span className="text-neutral-400">Payment Processed At:</span>
                                        <span className="text-neutral-200 font-mono">
                                            {new Date(selectedOrderDetails.start_time).toLocaleString()}
                                        </span>
                                    </div>
                                    {selectedOrderDetails.end_time && (
                                        <div className="flex justify-between">
                                            <span className="text-neutral-400">Active Until:</span>
                                            <span className="text-neutral-200 font-mono">
                                                {new Date(selectedOrderDetails.end_time).toLocaleString()}
                                            </span>
                                        </div>
                                    )}
                                    {selectedOrderDetails.pay_url && (
                                        <div className="flex justify-between pt-1.5 border-t border-dark-300/50">
                                            <span className="text-neutral-400">Invoice Payment Link:</span>
                                            <a 
                                                href={selectedOrderDetails.pay_url} 
                                                target="_blank" 
                                                rel="noreferrer" 
                                                className="text-primary-400 hover:underline font-mono truncate max-w-[280px]"
                                            >
                                                {selectedOrderDetails.pay_url}
                                            </a>
                                        </div>
                                    )}
                                </div>

                                {/* Additional Transaction Technical Info (if present) */}
                                {parsedDetails && parsedDetails.tx_info && (
                                    <div className="bg-dark-300/20 p-3 rounded-lg border border-dark-300/40 text-xs mb-5">
                                        <span className="text-neutral-400 block mb-1.5 font-semibold">Payment Gateway Technical Details:</span>
                                        <pre className="text-[11px] text-neutral-300 font-mono bg-dark-300/40 p-2 rounded overflow-x-auto">
                                            {JSON.stringify(parsedDetails.tx_info, null, 2)}
                                        </pre>
                                    </div>
                                )}

                                <div className="flex justify-end pt-2">
                                    <button
                                        onClick={() => setShowOrderDetailsModal(false)}
                                        className="btn btn-primary px-5 py-2 text-sm"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </div>
        </div>
    );
};

export default AdminPanelPage;